import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import liveChatApi from '../../services/liveChatApi';
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  Download,
  ExternalLink,
  LockKeyhole,
  Mail,
  RefreshCw,
  Save,
  Search,
  Send,
  Smartphone,
  Trash2,
  TrendingUp,
  X,
  ChevronRight,
} from '../../components/icons/agent/AgentPortalIconKit';
import {
  ActiveCustomerIcon,
  AnalyticsSectionIcon,
  BadgeDollarSign,
  BarChart3,
  Bell,
  CalendarDays,
  Camera,
  CheckCircle2,
  CircleDollarSign,
  Code2,
  CoinStackIcon,
  CreditsUsedIcon,
  Copy,
  CustomersGroupIcon,
  CustomerChatSectionIcon,
  EarningsSectionIcon,
  Eye,
  EyeOff,
  ExpensesReceiptIcon,
  Gamepad2,
  GamesSectionIcon,
  GitBranch,
  Headphones,
  HowItWorksIcon,
  HomeSectionIcon,
  IconTile,
  InviteAgentIcon,
  LeadCenterIcon,
  LifeBuoy,
  LineChart,
  Link2,
  LogOut,
  MessageCircle,
  MessageSquareText,
  MoneyFlowIcon,
  Moon,
  MoreSectionIcon,
  NavChatIcon,
  NavCustomersIcon,
  NavHomeIcon,
  NavMoreIcon,
  Network,
  NewJoinedIcon,
  ProfileSectionIcon,
  ReferralLinkIcon,
  ReferralTreeIcon,
  ReportsSectionIcon,
  ScrollText,
  ShieldCheck,
  Sun,
  TeamChatIcon,
  TransparentLedgerIcon,
  UserCog,
  Users,
  Wallet,
  WithdrawalHistoryIcon,
  WithdrawActionIcon,
} from '../../components/icons/agent/AgentPortalPngIconKit';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../components/ui/chart';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import ChatSupportPage from '../admin/ChatSupportPage';
import inviteNetworkHero from '../../assets/agent-portal/invite-network-hero.png';
import howItWorksFlow from '../../assets/agent-portal/how-it-works-flow.png';
import withdrawalReassurance from '../../assets/agent-portal/withdrawal-reassurance.png';
import earningsGrowthWallet from '../../assets/agent-portal/earnings-growth-wallet-spot.png';
import customersOwnedNetwork from '../../assets/agent-portal/customers-owned-network-spot.png';
import gameCreditEngine from '../../assets/agent-portal/game-credit-engine-spot.png';
import reportsInsightLens from '../../assets/agent-portal/reports-insight-lens-spot.png';
import customerLinksBeacon from '../../assets/agent-portal/customer-links-beacon-spot.png';
import ledgerProofTrail from '../../assets/agent-portal/ledger-proof-trail-spot.png';
import teamSupportChannels from '../../assets/agent-portal/team-support-channels-spot.png';
import moneyFlowPipeline from '../../assets/agent-portal/money-flow-pipeline-spot.png';
import expensesCutoffLayers from '../../assets/agent-portal/expenses-cutoff-layers-spot.png';
import payoutProofCaptureSpot from '../../assets/agent-portal/payout-proof-capture-spot.png';
import payoutHistoryEmptySpot from '../../assets/agent-portal/payout-history-empty-spot.png';
import withdrawalAmountStep from '../../assets/agent-portal/withdrawal-wizard/amount-step.png';
import withdrawalMethodStep from '../../assets/agent-portal/withdrawal-wizard/method-step.png';
import withdrawalDetailsStep from '../../assets/agent-portal/withdrawal-wizard/details-step.png';
import withdrawalReviewStep from '../../assets/agent-portal/withdrawal-wizard/review-step.png';
import analyticsFlowMapSpot from '../../assets/agent-portal/analytics-flow-map-spot.png';
import referralTreeGrowthSpot from '../../assets/agent-portal/referral-tree-growth-spot.png';
import chimePaymentIcon from '../../assets/agent-portal/payment-methods/chime-3d.png';
import paypalPaymentIcon from '../../assets/agent-portal/payment-methods/paypal-3d.png';
import cashAppPaymentIcon from '../../assets/agent-portal/payment-methods/cash-app-3d.png';
import venmoPaymentIcon from '../../assets/agent-portal/payment-methods/venmo-3d.png';
import applePayPaymentIcon from '../../assets/agent-portal/payment-methods/apple-pay-3d.png';
import googlePayPaymentIcon from '../../assets/agent-portal/payment-methods/google-pay-3d.png';
import './StaffPortalReference.css';
import './StaffPortalConcept.css';
import './StaffPortalV2.css';

const usdFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtUSD = (value) => usdFormatter.format(Number(value || 0));
const fmtPct = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const fmtDate = (value) => value ? new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const fmtShortDate = (value) => value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not available';
const fmtLongDay = (value = new Date()) => value.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const greetingFor = (value = new Date()) => {
  const hour = value.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const AGENT_GAME_ICON_BASE = '/assets/games/checkmycasino';
const AGENT_GAME_ICONS = {
  ultrapanda: `${AGENT_GAME_ICON_BASE}/ultra-panda.jpeg`,
  milkyway: `${AGENT_GAME_ICON_BASE}/milky-way.jpeg`,
  juwa2: `${AGENT_GAME_ICON_BASE}/juwa-2.jpg`,
  juwa: `${AGENT_GAME_ICON_BASE}/juwa.jpeg`,
  gamevault: `${AGENT_GAME_ICON_BASE}/game-vault.jpeg`,
  gamevault999: `${AGENT_GAME_ICON_BASE}/game-vault.jpeg`,
  firekirin: `${AGENT_GAME_ICON_BASE}/fire-kirin.jpeg`,
  vblink: `${AGENT_GAME_ICON_BASE}/vblink.jpeg`,
  orionstars: `${AGENT_GAME_ICON_BASE}/orion-stars.jpeg`,
  pandamaster: `${AGENT_GAME_ICON_BASE}/panda-master.jpeg`,
  cashmachine: `${AGENT_GAME_ICON_BASE}/cash-machine.jpg`,
  gameroom: `${AGENT_GAME_ICON_BASE}/gameroom.png`,
  gameroom777: `${AGENT_GAME_ICON_BASE}/gameroom.png`,
  gamerroom: `${AGENT_GAME_ICON_BASE}/gameroom.png`,
  mafia: `${AGENT_GAME_ICON_BASE}/mafia.jpg`,
  yolo: `${AGENT_GAME_ICON_BASE}/yolo.jpg`,
  noble: `${AGENT_GAME_ICON_BASE}/noble.png`,
  lasvegassweeps: `${AGENT_GAME_ICON_BASE}/vegas-sweeps.jpeg`,
  vegassweeps: `${AGENT_GAME_ICON_BASE}/vegas-sweeps.jpeg`,
  vegasroll: `${AGENT_GAME_ICON_BASE}/vegas-roll.png`,
  cashfrenzy: `${AGENT_GAME_ICON_BASE}/cash-frenzy.jpeg`,
  mrallinone: `${AGENT_GAME_ICON_BASE}/mr-all-in-one.png`,
  riversweep: `${AGENT_GAME_ICON_BASE}/riversweeps.png`,
  riversweeps: `${AGENT_GAME_ICON_BASE}/riversweeps.png`,
};

const normalizeAgentGameKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const getAgentGameName = (game) => game?.name || game?.game_name || game?.code || game?.game_code || game?.id || game?.game_id || 'Game';
const getAgentGameIcon = (game) => {
  const candidates = [game?.id, game?.code, game?.game_id, game?.game_code, game?.name, game?.game_name];
  return candidates.map(normalizeAgentGameKey).map((key) => AGENT_GAME_ICONS[key]).find(Boolean) || '';
};

function AgentGameIcon({ game, compact = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  const iconUrl = getAgentGameIcon(game);
  const gameName = getAgentGameName(game);

  useEffect(() => {
    setImageFailed(false);
  }, [iconUrl]);

  return (
    <span className={`agent-game-icon${compact ? ' is-compact' : ''}`} aria-hidden={!iconUrl || imageFailed ? 'true' : undefined}>
      {iconUrl && !imageFailed
        ? <img src={iconUrl} alt={`${gameName} logo`} loading="lazy" decoding="async" onError={() => setImageFailed(true)} />
        : <Gamepad2 aria-hidden="true" />}
    </span>
  );
}

const AGENT_THEME_KEY = 'agent-portal-theme';
const AGENT_PULL_REFRESH_THRESHOLD = 68;
const AGENT_INITIAL_SPLASH_MS = 3000;
const AGENT_PULL_REFRESH_MAX = 96;
const WITHDRAWAL_BOOKKEEPING_REASONS = new Set(['WITHDRAWAL_RESERVED', 'WITHDRAWAL_APPROVED', 'WITHDRAWAL_REFUND']);
const REPORT_COLORS = ['#16a34a', '#2563eb', '#f59e0b', '#7c3aed', '#e11d48', '#94a3b8'];

const PAYMENT_METHOD_VISUALS = {
  chime: { label: 'Chime', icon: chimePaymentIcon },
  paypal: { label: 'PayPal', icon: paypalPaymentIcon },
  cashapp: { label: 'Cash App', icon: cashAppPaymentIcon },
  venmo: { label: 'Venmo', icon: venmoPaymentIcon },
  applepay: { label: 'Apple Pay', icon: applePayPaymentIcon },
  googlepay: { label: 'Google Pay', icon: googlePayPaymentIcon },
};

const paymentMethodVisualFor = (value) => {
  const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!normalized) return null;
  if (normalized.includes('chime')) return PAYMENT_METHOD_VISUALS.chime;
  if (normalized.includes('paypal')) return PAYMENT_METHOD_VISUALS.paypal;
  if (normalized.includes('cashapp') || normalized.includes('ecash')) return PAYMENT_METHOD_VISUALS.cashapp;
  if (normalized.includes('venmo')) return PAYMENT_METHOD_VISUALS.venmo;
  if (normalized.includes('applepay')) return PAYMENT_METHOD_VISUALS.applepay;
  if (normalized.includes('googlepay') || normalized === 'gpay') return PAYMENT_METHOD_VISUALS.googlepay;
  return null;
};

const formatLedgerEventLabel = (value, fallback = 'Ledger event') => {
  const raw = String(value || fallback);
  const reason = raw.toUpperCase();
  if (reason === 'NET_HOUSE_FEE') return 'Coin expenses';
  if (reason === 'NET_HOUSE_FEE_REVERSAL') return 'Coin expenses reversal';
  return raw.replaceAll('_', ' ');
};

const formatDayLabel = (day) => {
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const getDailyEarningSeries = (analytics, days = 30) => (analytics?.daily || [])
  .slice(-days)
  .map((row) => ({
    key: row.day,
    label: formatDayLabel(row.day),
    profit: Number(row.earned_usd || 0),
  }));

function AgentWelcomeAvatar({ agent, name, onUpload, uploading = false, disabled = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  const avatarUrl = agent?.agent_chat_profile?.avatar_url || agent?.avatar_url || '';
  const initial = String(name || 'A').trim().charAt(0).toUpperCase() || 'A';
  const hasAvatar = Boolean(avatarUrl) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  const avatarContents = hasAvatar
    ? <img src={avatarUrl} alt="" onError={() => setImageFailed(true)} />
    : <><span aria-hidden="true">{initial}</span><i aria-hidden="true"><Camera /></i></>;

  if (onUpload) {
    return (
      <button
        type="button"
        className={`agent-welcome-avatar is-uploadable${hasAvatar ? ' has-image' : ' is-empty'}`}
        onClick={onUpload}
        disabled={disabled || uploading}
        aria-label={hasAvatar ? 'Change profile photo' : 'Upload profile photo'}
        title={hasAvatar ? 'Change profile photo' : 'Upload profile photo'}
      >
        {avatarContents}
      </button>
    );
  }

  return (
    <span className="agent-welcome-avatar" aria-label={`${name || 'Agent'} profile`}>
      {avatarContents}
    </span>
  );
}

// Real daily earnings from the backend series — never fabricated client-side.
const AGENT_SECTIONS = [
  { id: 'overview', label: 'Home', icon: HomeSectionIcon, tone: 'green', description: 'Customer, activity, and earning summary.' },
  { id: 'leads', label: 'Lead Center', icon: LeadCenterIcon, tone: 'violet', description: 'Review campaign leads and plan each follow-up.' },
  { id: 'earnings', label: 'Your Earnings', icon: EarningsSectionIcon, tone: 'emerald', description: 'Lifetime earnings, daily history, and earnings trends.' },
  { id: 'games', label: 'Game Credits Activity', icon: GamesSectionIcon, tone: 'amber', description: 'Review credits used by game; open Coin Rates when you need the assigned percentages.' },
  { id: 'invite-agent', label: 'Invite an Agent', icon: InviteAgentIcon, tone: 'violet', description: 'Earn extra 5% of their net profit.' },
  { id: 'reports', label: 'Reports', icon: ReportsSectionIcon, tone: 'blue', description: 'Percent reports and game-wise coin tracking.' },
  { id: 'analytics', label: 'Analytics', icon: AnalyticsSectionIcon, tone: 'violet', description: 'Daily charts, trends, and game-wise coin costs.' },
  { id: 'moneyflow', label: 'Money Flow', icon: MoneyFlowIcon, tone: 'cyan', description: 'Where the money goes, filtered by day, week, or month.' },
  { id: 'fees', label: 'Expenses', icon: ExpensesReceiptIcon, tone: 'rose', description: 'Every fee and game cost that applies to you.' },
  { id: 'links', label: 'Referral Links', icon: ReferralLinkIcon, tone: 'cyan', description: 'Share links and track assigned schemes.' },
  { id: 'players', label: 'Customers', icon: CustomersGroupIcon, tone: 'blue', description: 'Direct and referral-tree customer list.' },
  { id: 'referrals', label: 'Referral Tree', icon: ReferralTreeIcon, tone: 'violet', description: 'See customers who joined through your network.' },
  { id: 'chat', label: 'Customer Chat', icon: CustomerChatSectionIcon, tone: 'cyan', description: 'Talk with players attributed to your agent link.' },
  { id: 'team-chat', label: 'Team Chat', icon: TeamChatIcon, tone: 'violet', description: 'Contact support, confirmations, managers, and developers.' },
  { id: 'ledger', label: 'Transparent Ledger', icon: TransparentLedgerIcon, tone: 'blue', description: 'Recent credits, debits, and earning events.' },
  { id: 'withdrawals', label: 'Withdrawals', icon: WithdrawalHistoryIcon, tone: 'amber', description: 'Requests, history, and payout methods.' },
  { id: 'profile', label: 'Profile', icon: ProfileSectionIcon, tone: 'green', description: 'Private profile and live-chat bio.' },
  { id: 'how', label: 'How It Works', icon: HowItWorksIcon, tone: 'emerald', description: 'Simple guide for earning and support flow.' },
  { id: 'more', label: 'More', icon: MoreSectionIcon, tone: 'green', description: 'All your tools and settings.', mobileOnly: true },
];
const AGENT_PRIMARY_SECTIONS = ['overview', 'leads', 'players', 'chat', 'earnings'];
const AGENT_DESKTOP_BUSINESS_SECTIONS = ['games', 'withdrawals', 'invite-agent', 'ledger'];
const AGENT_MOBILE_SECTIONS = ['overview', 'players', 'chat', 'more'];
const AGENT_MOBILE_ICON_BY_ID = {
  overview: NavHomeIcon,
  players: NavCustomersIcon,
  chat: NavChatIcon,
  more: NavMoreIcon,
};
const TEAM_ICONS = {
  communication: Headphones,
  customer_support: LifeBuoy,
  confirmation: ShieldCheck,
  managers: UserCog,
  development: Code2,
};
const ADMIN_PREVIEW_ROLES = new Set(['admin', 'tenant_admin', 'moderator', 'staff']);
const WITHDRAWAL_STEPS = [
  { id: 'amount', label: 'Amount', short: 'Enter amount', icon: withdrawalAmountStep },
  { id: 'method', label: 'Method', short: 'Choose payout', icon: withdrawalMethodStep },
  { id: 'details', label: 'Details', short: 'Photo and note', icon: withdrawalDetailsStep },
  { id: 'review', label: 'Review', short: 'Confirm request', icon: withdrawalReviewStep },
];
const WITHDRAWAL_STEP_COPY = {
  amount: {
    title: 'How much would you like to withdraw?',
    description: 'Enter your amount in USD. We will check it against your available balance before you continue.',
  },
  method: {
    title: 'Choose your payout method',
    description: 'Select where you want the payout sent. Method-specific limits are checked automatically.',
  },
  details: {
    title: 'Add payout details',
    description: 'Attach optional proof and add the complete account details or instructions the payout team needs.',
  },
  review: {
    title: 'Review your withdrawal',
    description: 'Check every detail before the request is reserved and sent to the payout team.',
  },
};
const WITHDRAWAL_METHOD_ICONS = {
  bank_transfer: '/assets/player-v2/icons/ocean-kit/bank.webp',
  bank_deposit: '/assets/player-v2/icons/ocean-kit/deposit.webp',
  usdt: '/payment-logos/usdt.svg',
  btc: '/payment-logos/bitcoin.svg',
};
const withdrawalMethodId = (method) => method?.id || method?.label || String(method || '');
const withdrawalMethodLabel = (method) => method?.label || method?.name || String(method || '');
const withdrawalMethodIcon = (method) => WITHDRAWAL_METHOD_ICONS[withdrawalMethodId(method)] || withdrawalMethodStep;

export default function StaffPortalPage() {
  const { user, logout, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [methods, setMethods] = useState([]);
  const [withdrawalHighValueMinimumNpr, setWithdrawalHighValueMinimumNpr] = useState(200000);
  const [loading, setLoading] = useState(true);
  const initialSplashStartedAtRef = useRef(Date.now());
  const initialSplashFinishedRef = useRef(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount_usd: '', method: '', photo_url: '', note: '' });
  const [withdrawStep, setWithdrawStep] = useState('amount');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [withdrawPhotoUploading, setWithdrawPhotoUploading] = useState(false);
  const withdrawPhotoInputRef = useRef(null);
  const withdrawDialogRef = useRef(null);
  const withdrawHeadingRef = useRef(null);
  const withdrawTriggerRef = useRef(null);
  const withdrawSubmittingRef = useRef(false);
  const agentMainRef = useRef(null);
  const pullStartRef = useRef({ x: 0, y: 0 });
  const pullDistanceRef = useRef(0);
  const pullGestureActiveRef = useRef(false);
  const pullRefreshInFlightRef = useRef(false);
  const pullResetTimerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [pullRefreshStatus, setPullRefreshStatus] = useState('idle');
  const [portalSearch, setPortalSearch] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [chatUnreadCounts, setChatUnreadCounts] = useState({ customers: 0, team: 0 });
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return window.localStorage.getItem(AGENT_THEME_KEY) || 'light';
  });
  const canAdminPreview = Boolean(user?.is_master_admin || ADMIN_PREVIEW_ROLES.has(user?.role));

  useEffect(() => {
    window.localStorage.setItem(AGENT_THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    withdrawSubmittingRef.current = withdrawSubmitting;
  }, [withdrawSubmitting]);

  useEffect(() => {
    if (!withdrawOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !withdrawSubmittingRef.current) {
        setWithdrawOpen(false);
        setWithdrawStep('amount');
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(withdrawDialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || []).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!withdrawDialogRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      if (withdrawTriggerRef.current?.isConnected) withdrawTriggerRef.current.focus();
    };
  }, [withdrawOpen]);

  useEffect(() => {
    if (!withdrawOpen) return undefined;
    const frame = window.requestAnimationFrame(() => withdrawHeadingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [withdrawOpen, withdrawStep]);

  const loadAdminPreview = useCallback(async () => {
    const [overview, playerRes] = await Promise.all([
      api.get('/api/admin/agent-earn/overview'),
      api.get('/api/admin/agent-earn/players').catch(() => ({ data: { players: [] } })),
    ]);
    const assignments = overview.data?.assignments || [];
    const agents = overview.data?.agents || [];
    const players = playerRes.data?.players || [];
    const agentById = new Map(agents.map((item) => [item.id, item]));
    const primaryAgent = agentById.get(assignments[0]?.agent_id) || agents[0] || {};
    const playerSummaryReady = players.length > 0;
    const depositsUsd = players.reduce((sum, item) => sum + Number(item.deposits_usd || 0), 0);
    const withdrawalsUsd = players.reduce((sum, item) => sum + Number(item.withdrawals_usd || 0), 0);
    setData({
      agent: {
        ...primaryAgent,
        display_name: `${primaryAgent.full_name || primaryAgent.username || primaryAgent.email || user?.username || 'Admin'} preview`,
      },
      assignments,
      players,
      ledger: [],
      withdrawals: [],
      admin_preview: true,
      summary: {
        customers_total: playerSummaryReady ? players.length : agents.reduce((sum, item) => sum + Number(item.active_players || 0), 0),
        players_total: playerSummaryReady ? players.length : agents.reduce((sum, item) => sum + Number(item.active_players || 0), 0),
        players_active: playerSummaryReady ? players.filter((item) => item.active !== false).length : agents.reduce((sum, item) => sum + Number(item.active_players || 0), 0),
        direct_players: playerSummaryReady ? players.filter((item) => item.source !== 'referral_tree').length : 0,
        tree_players: playerSummaryReady ? players.filter((item) => item.source === 'referral_tree').length : 0,
        deposits_usd: depositsUsd,
        withdrawals_usd: withdrawalsUsd,
        net_revenue_usd: depositsUsd - withdrawalsUsd,
        revenue_usd: depositsUsd - withdrawalsUsd,
        net_profit_usd: depositsUsd - withdrawalsUsd,
        balance_usd: agents.reduce((sum, item) => sum + Number(item.balance_usd || 0), 0),
        lifetime_credits_usd: agents.reduce((sum, item) => sum + Number(item.lifetime_credits_usd || 0), 0),
      },
      verification_summary: {
        total: players.length,
        email_verified: players.filter((item) => item.email_verified).length,
        phone_verified: players.filter((item) => item.phone_verified).length,
        email_verified_percent: players.length ? (players.filter((item) => item.email_verified).length / players.length) * 100 : 0,
        phone_verified_percent: players.length ? (players.filter((item) => item.phone_verified).length / players.length) * 100 : 0,
      },
      report_summary: {
        in_percent: depositsUsd + withdrawalsUsd > 0 ? (depositsUsd / (depositsUsd + withdrawalsUsd)) * 100 : 0,
        out_percent: depositsUsd + withdrawalsUsd > 0 ? (withdrawalsUsd / (depositsUsd + withdrawalsUsd)) * 100 : 0,
        customers_with_revenue: players.filter((item) => Number(item.net_revenue_usd || 0) !== 0).length,
        positive_customers: players.filter((item) => Number(item.net_revenue_usd || 0) > 0).length,
        negative_customers: players.filter((item) => Number(item.net_revenue_usd || 0) < 0).length,
      },
      coin_tracking: [],
      referral_tree: { nodes: [], root_count: 0, tree_count: 0, max_depth: 0 },
      agent_referral: { unlocked: false, required_unlock: 'Security deposit / investment unlock required', commission_percent: 5, referred_agents_count: 0, referred_agents: [] },
      how_it_works: [],
      team_channels: [],
      period: 'admin_preview',
    });
  }, [user?.username]);

  const fetchData = useCallback(async () => {
    try {
      const [dashboard, methodRes, playerRes, monthEarningsRes] = await Promise.all([
        api.get('/api/agent-earn/me/dashboard'),
        api.get('/api/agent-earn/me/withdrawal-methods').catch(() => ({ data: { methods: [] } })),
        api.get('/api/agent-earn/me/players').catch(() => ({ data: null })),
        api.get('/api/agent-earn/me/earnings?range=month').catch(() => ({ data: null })),
      ]);
      setData({
        ...dashboard.data,
        month_earnings: monthEarningsRes.data,
        players: Array.isArray(playerRes.data?.players) ? playerRes.data.players : (dashboard.data?.players || []),
      });
      const nextMethods = methodRes.data?.methods || [];
      setMethods(nextMethods);
      setWithdrawalHighValueMinimumNpr(Number(methodRes.data?.high_value_minimum_npr || 200000));
      setWithdrawForm((prev) => prev.method || !nextMethods[0]
        ? prev
        : { ...prev, method: nextMethods[0].id || nextMethods[0].label || String(nextMethods[0]) });
    } catch (err) {
      if (err.response?.status === 403 && canAdminPreview) {
        try {
          await loadAdminPreview();
          return;
        } catch (previewErr) {
          toast.error(previewErr.response?.data?.detail || 'Agent links preview could not load');
          return;
        }
      }
      toast.error(err.response?.data?.detail || 'Agent portal could not load');
    } finally {
      if (!initialSplashFinishedRef.current) {
        const remainingMs = Math.max(0, AGENT_INITIAL_SPLASH_MS - (Date.now() - initialSplashStartedAtRef.current));
        if (remainingMs) await new Promise((resolve) => window.setTimeout(resolve, remainingMs));
        initialSplashFinishedRef.current = true;
      }
      setLoading(false);
    }
  }, [canAdminPreview, loadAdminPreview]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const players = data?.players || [];
  const assignments = data?.assignments || [];
  const ledger = data?.ledger || [];
  const withdrawals = data?.withdrawals || [];
  const agent = data?.agent || {};
  const verificationSummary = data?.verification_summary || {};
  const reportSummary = data?.report_summary || {};
  const coinTracking = data?.coin_tracking || [];
  const customerActivity = data?.customer_activity || [];
  const pointsMovements = data?.points_movements || [];
  const referralTree = data?.referral_tree || { nodes: [] };
  const analytics = data?.analytics || null;
  const agentReferral = data?.agent_referral || {};
  const howItWorks = data?.how_it_works || [];
  const teamChannels = data?.team_channels || [];
  const currentWithdrawalStep = WITHDRAWAL_STEPS.find((step) => step.id === withdrawStep) || WITHDRAWAL_STEPS[0];
  const currentWithdrawalStepIndex = WITHDRAWAL_STEPS.findIndex((step) => step.id === currentWithdrawalStep.id);
  const selectedWithdrawalMethod = methods.find((method) => withdrawalMethodId(method) === withdrawForm.method);

  const activeSection = useMemo(() => {
    const requested = searchParams.get('section') || 'overview';
    return AGENT_SECTIONS.some((section) => section.id === requested) ? requested : 'overview';
  }, [searchParams]);

  const activeMeta = AGENT_SECTIONS.find((section) => section.id === activeSection) || AGENT_SECTIONS[0];
  const portalAdminPreview = Boolean(data?.admin_preview);

  const updateCustomerUnread = useCallback((count) => {
    setChatUnreadCounts((current) => ({ ...current, customers: Math.max(0, Number(count) || 0) }));
  }, []);

  const updateTeamUnread = useCallback((count) => {
    setChatUnreadCounts((current) => ({ ...current, team: Math.max(0, Number(count) || 0) }));
  }, []);

  const loadChatUnreadCounts = useCallback(async () => {
    if (portalAdminPreview) {
      setChatUnreadCounts({ customers: 0, team: 0 });
      return;
    }
    const [customerResult, teamResult] = await Promise.allSettled([
      liveChatApi.get('/api/chat/conversations?status=all&limit=1000'),
      api.get('/api/agent-earn/me/team-chat'),
    ]);
    setChatUnreadCounts((current) => {
      const rooms = customerResult.status === 'fulfilled'
        ? (customerResult.value.data?.conversations || [])
        : null;
      const teamConversations = teamResult.status === 'fulfilled'
        ? (teamResult.value.data?.conversations || [])
        : null;
      const serverTeamUnread = teamResult.status === 'fulfilled'
        ? teamResult.value.data?.unread_count
        : null;
      return {
        customers: rooms
          ? rooms.reduce((sum, room) => sum + Math.max(0, Number(room.staff_unread) || 0), 0)
          : current.customers,
        team: teamResult.status === 'fulfilled'
          ? Math.max(0, serverTeamUnread == null
            ? teamConversations.reduce(
              (sum, conversation) => sum + Math.max(0, Number(conversation.agent_unread) || 0),
              0,
            )
            : Number(serverTeamUnread) || 0)
          : current.team,
      };
    });
  }, [portalAdminPreview]);

  useEffect(() => {
    if (!data || portalAdminPreview) return undefined;
    loadChatUnreadCounts();
    const interval = window.setInterval(loadChatUnreadCounts, 15000);
    return () => window.clearInterval(interval);
  }, [data, loadChatUnreadCounts, portalAdminPreview]);

  const resetPullRefresh = useCallback(() => {
    pullGestureActiveRef.current = false;
    pullDistanceRef.current = 0;
    setPullDistance(0);
    setPullRefreshStatus('idle');
  }, []);

  const runPullRefresh = useCallback(async () => {
    if (pullRefreshInFlightRef.current) return;
    pullRefreshInFlightRef.current = true;
    pullGestureActiveRef.current = false;
    pullDistanceRef.current = 54;
    setPullDistance(54);
    setPullRefreshStatus('refreshing');
    try {
      await fetchData();
    } finally {
      pullRefreshInFlightRef.current = false;
      if (pullResetTimerRef.current) window.clearTimeout(pullResetTimerRef.current);
      pullResetTimerRef.current = window.setTimeout(resetPullRefresh, 420);
    }
  }, [fetchData, resetPullRefresh]);

  const handlePullStart = useCallback((event) => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    const main = agentMainRef.current;
    const touch = event.touches?.[0];
    const blockedSection = activeSection === 'chat' || activeSection === 'team-chat';
    const blockedTarget = typeof Element !== 'undefined' && event.target instanceof Element
      && Boolean(event.target.closest('[role="dialog"], input, textarea, select, button, a, [contenteditable="true"], [data-pull-refresh-ignore]'));
    if (!isMobile || !main || !touch || event.touches.length !== 1 || main.scrollTop > 1 || blockedSection || blockedTarget || withdrawOpen || pullRefreshInFlightRef.current) {
      pullGestureActiveRef.current = false;
      return;
    }
    pullStartRef.current = { x: touch.clientX, y: touch.clientY };
    pullGestureActiveRef.current = true;
  }, [activeSection, withdrawOpen]);

  const handlePullMove = useCallback((event) => {
    if (!pullGestureActiveRef.current || pullRefreshInFlightRef.current) return;
    const main = agentMainRef.current;
    const touch = event.touches?.[0];
    if (!main || !touch || main.scrollTop > 1) {
      resetPullRefresh();
      return;
    }
    const deltaY = touch.clientY - pullStartRef.current.y;
    const deltaX = Math.abs(touch.clientX - pullStartRef.current.x);
    if (deltaY <= 0 || deltaX > deltaY) {
      resetPullRefresh();
      return;
    }
    if (event.cancelable) event.preventDefault();
    const nextDistance = Math.min(AGENT_PULL_REFRESH_MAX, deltaY * 0.52);
    pullDistanceRef.current = nextDistance;
    setPullDistance(nextDistance);
    setPullRefreshStatus(nextDistance >= AGENT_PULL_REFRESH_THRESHOLD ? 'release' : 'pulling');
  }, [resetPullRefresh]);

  const handlePullEnd = useCallback(() => {
    if (!pullGestureActiveRef.current || pullRefreshInFlightRef.current) return;
    if (pullDistanceRef.current >= AGENT_PULL_REFRESH_THRESHOLD) {
      runPullRefresh();
      return;
    }
    resetPullRefresh();
  }, [resetPullRefresh, runPullRefresh]);

  useEffect(() => {
    resetPullRefresh();
  }, [activeSection, resetPullRefresh]);

  useEffect(() => () => {
    if (pullResetTimerRef.current) window.clearTimeout(pullResetTimerRef.current);
  }, []);

  const agentName = agent.display_name || agent.agent_chat_profile?.display_name || agent.full_name || agent.username || agent.public_identity || 'Agent';
  const partnerSince = fmtShortDate(agent.created_at);
  const mobileSections = AGENT_SECTIONS.filter((section) => AGENT_MOBILE_SECTIONS.includes(section.id));
  const primarySections = AGENT_SECTIONS.filter((section) => AGENT_PRIMARY_SECTIONS.includes(section.id));
  const desktopBusinessSections = AGENT_SECTIONS.filter((section) => AGENT_DESKTOP_BUSINESS_SECTIONS.includes(section.id));
  const allToolSections = AGENT_SECTIONS.filter((section) => !section.mobileOnly);
  const moreSection = AGENT_SECTIONS.find((section) => section.id === 'more');
  const moreSidebarActive = !AGENT_PRIMARY_SECTIONS.includes(activeSection) && !AGENT_DESKTOP_BUSINESS_SECTIONS.includes(activeSection);
  const mobileMoreActive = !['overview', 'players', 'chat'].includes(activeSection);
  const mobileBackAvailable = mobileMoreActive && activeSection !== 'more';
  const notificationItems = [
    ...ledger.slice(0, 3).map((row) => ({
      id: `ledger-${row.id}`,
      label: formatLedgerEventLabel(row.reason || row.event_type, 'Account activity'),
      detail: fmtDate(row.created_at),
    })),
    ...withdrawals.slice(0, 2).map((row) => ({
      id: `withdrawal-${row.id}`,
      label: `Withdrawal ${String(row.status || 'pending').toLowerCase()}`,
      detail: `${fmtUSD(row.amount_usd)} · ${fmtDate(row.created_at)}`,
    })),
  ].slice(0, 5);
  const pullRefreshLabel = pullRefreshStatus === 'refreshing'
    ? 'Refreshing workspace'
    : pullRefreshStatus === 'release'
      ? 'Release to refresh'
      : 'Pull to refresh';
  const pullRefreshProgress = Math.min(pullDistance / AGENT_PULL_REFRESH_THRESHOLD, 1);

  const setSection = (sectionId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (sectionId === 'overview') nextParams.delete('section');
    else nextParams.set('section', sectionId);
    if (sectionId !== 'chat') nextParams.delete('player_id');
    setSearchParams(nextParams);
    setNotificationsOpen(false);
  };

  const submitPortalSearch = (event) => {
    event.preventDefault();
    const query = portalSearch.trim().toLowerCase();
    if (!query) return;
    const match = AGENT_SECTIONS.find((section) => !section.mobileOnly && `${section.label} ${section.description}`.toLowerCase().includes(query));
    if (match) {
      setSection(match.id);
      setPortalSearch('');
    } else {
      toast.error('No portal section matched that search');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const copy = async (text, label = 'Copied') => {
    const value = String(text || '');
    try {
      await navigator.clipboard.writeText(value);
      toast.success(label);
    } catch {
      const fallback = document.createElement('textarea');
      fallback.value = value;
      fallback.setAttribute('readonly', '');
      fallback.style.position = 'fixed';
      fallback.style.opacity = '0';
      document.body.appendChild(fallback);
      fallback.select();
      const copied = document.execCommand('copy');
      fallback.remove();
      if (copied) toast.success(label);
      else toast.error('Copy was blocked by this browser');
    }
  };

  const removePlayer = async (player) => {
    if (!window.confirm(`Remove ${player.username || 'this player'} from your future earnings list? They can still play.`)) return;
    try {
      await api.post(`/api/agent-earn/me/players/${player.player_id}/remove`);
      toast.success('Player removed from future earnings');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove player');
    }
  };

  const openPlayerChat = (player) => {
    const playerId = String(player?.player_id || player?.id || '').trim();
    if (!playerId) {
      toast.error('This customer does not have a player ID yet');
      return;
    }
    // ChatSupportPage resolves this ID through the scope-protected direct
    // conversation endpoint. Never try to match a customer by their name.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('section', 'chat');
    nextParams.set('player_id', playerId);
    setSearchParams(nextParams);
    setNotificationsOpen(false);
  };

  const submitWithdrawal = async () => {
    if (withdrawSubmitting) return;
    try {
      setWithdrawSubmitting(true);
      await api.post('/api/agent-earn/me/withdrawals', {
        amount_usd: Number(withdrawForm.amount_usd || 0),
        method: withdrawForm.method,
        photo_url: withdrawForm.photo_url || null,
        note: withdrawForm.note || null,
      });
      toast.success('Withdrawal request sent');
      setWithdrawOpen(false);
      setWithdrawStep('amount');
      setWithdrawForm((prev) => ({ ...prev, amount_usd: '', photo_url: '', note: '' }));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal request failed');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const openWithdrawal = () => {
    withdrawTriggerRef.current = document.activeElement;
    setWithdrawStep('amount');
    setWithdrawOpen(true);
  };

  const closeWithdrawal = () => {
    if (withdrawSubmitting) return;
    setWithdrawOpen(false);
    setWithdrawStep('amount');
  };

  const validateWithdrawalAmount = () => {
    const amount = Number(withdrawForm.amount_usd || 0);
    const available = Number(analytics?.wallet?.available_usd ?? summary.balance_usd ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid withdrawal amount');
      return false;
    }
    if (available >= 0 && amount > available) {
      toast.error(`The amount cannot exceed your available balance of ${fmtUSD(available)}`);
      return false;
    }
    return true;
  };

  const validateWithdrawalMethod = () => {
    const amount = Number(withdrawForm.amount_usd || 0);
    if (!withdrawForm.method) {
      toast.error('Choose a payout method');
      return false;
    }
    if (['bank_deposit', 'cash_drop'].includes(withdrawForm.method)) {
      const nprRate = Number(analytics?.fee_rules?.npr_per_usd || 0);
      if (nprRate > 0 && amount * nprRate <= withdrawalHighValueMinimumNpr) {
        toast.error(`This payout method requires more than NPR ${withdrawalHighValueMinimumNpr.toLocaleString()}`);
        return false;
      }
    }
    return true;
  };

  const continueWithdrawalAmount = () => {
    if (validateWithdrawalAmount()) setWithdrawStep('method');
  };

  const continueWithdrawalMethod = () => {
    if (validateWithdrawalAmount() && validateWithdrawalMethod()) setWithdrawStep('details');
  };

  const reviewWithdrawal = () => {
    if (!validateWithdrawalAmount() || !validateWithdrawalMethod()) return;
    setWithdrawStep('review');
  };

  const uploadWithdrawalPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choose a JPG, PNG, or WEBP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be 5 MB or smaller');
      return;
    }
    const payload = new FormData();
    payload.append('file', file);
    try {
      setWithdrawPhotoUploading(true);
      const response = await api.post('/api/agent-earn/me/withdrawals/photo', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const photoUrl = response.data?.photo_url;
      if (!photoUrl) throw new Error('Photo URL was not returned');
      setWithdrawForm((current) => ({ ...current, photo_url: photoUrl }));
      toast.success('Photo added');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Photo could not be uploaded');
    } finally {
      setWithdrawPhotoUploading(false);
    }
  };

  const refreshAfterProfileSave = async () => {
    await Promise.all([
      fetchData(),
      checkAuth?.(),
    ]);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'reports':
        return (
          <DetailedReportsSection
            players={players}
            coinTracking={coinTracking}
            pointsMovements={pointsMovements}
            customerTotal={summary.customers_total}
          />
        );
      case 'profile':
        return (
          <ProfileSection
            agent={agent}
            adminPreview={Boolean(data?.admin_preview)}
            onSaved={refreshAfterProfileSave}
          />
        );
      case 'more':
        return (
          <MoreSection
            agent={agent}
            agentName={agentName}
            theme={theme}
            sections={allToolSections}
            onSection={setSection}
            onThemeChange={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
            onLogout={handleLogout}
          />
        );
      case 'links':
        return (
          <TenantLinksSection
            assignments={assignments}
            adminPreview={Boolean(data?.admin_preview)}
            onCopy={copy}
          />
        );
      case 'players':
        return <PlayersSection players={players} onRemove={removePlayer} onChat={openPlayerChat} adminPreview={Boolean(data?.admin_preview)} theme={theme} />;
      case 'leads':
        return <LeadCenterSection />;
      case 'referrals':
        return <ReferralSection referralTree={referralTree} />;
      case 'invite-agent':
        return <InviteAgentSection agentReferral={agentReferral} onCopy={copy} />;
      case 'chat':
        return <AgentChatSection adminPreview={portalAdminPreview} onSection={setSection} unreadCounts={chatUnreadCounts} onUnreadCountChange={updateCustomerUnread} />;
      case 'team-chat':
        return (
          <TeamChatSection
            adminPreview={Boolean(data?.admin_preview)}
            channels={teamChannels}
            onSection={setSection}
            unreadCounts={chatUnreadCounts}
            onTeamUnreadChange={updateTeamUnread}
          />
        );
      case 'ledger':
        return <LedgerSection ledger={ledger} customerActivity={customerActivity} pointsMovements={pointsMovements} />;
      case 'withdrawals':
        return (
          <WithdrawalsSection
            withdrawals={withdrawals}
            balance={summary.balance_usd}
            wallet={analytics?.wallet}
            onWithdraw={openWithdrawal}
          />
        );
      case 'analytics':
        return <AgentAnalyticsSection analytics={analytics} />;
      case 'games':
        return <GameRatesSection analytics={analytics} />;
      case 'earnings':
        return <YourEarningsSection analytics={analytics} summary={summary} adminPreview={Boolean(data?.admin_preview)} />;
      case 'moneyflow':
        return <MoneyFlowSection analytics={analytics} commissionPercent={summary.commission_percent} />;
      case 'fees':
        return <FeesSection analytics={analytics} />;
      case 'how':
        return <HowItWorksSection items={howItWorks} />;
      case 'overview':
      default:
        return (
          <OverviewSection
            agent={agent}
            summary={summary}
            verificationSummary={verificationSummary}
            reportSummary={reportSummary}
            players={players}
            assignments={assignments}
            ledger={ledger}
            withdrawals={withdrawals}
            analytics={analytics}
            monthEarnings={data?.month_earnings}
            adminPreview={Boolean(data?.admin_preview)}
            theme={theme}
            onThemeChange={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
            onSection={setSection}
            onWithdraw={() => setSection('withdrawals')}
            onProfileSaved={refreshAfterProfileSave}
            onCopy={copy}
          />
        );
    }
  };

  if (loading) return (
    <div className="agent-portal-loading" role="status" aria-live="polite">
      <div className="agent-portal-loading-mark" aria-hidden="true">
        <img className="agent-portal-loading-mark-base" src="/assets/so-agents/pull-refresh-mark.png" alt="" />
        <img className="agent-portal-loading-mark-glow" src="/assets/so-agents/pull-refresh-mark.png" alt="" />
      </div>
      <div className="agent-portal-loading-copy">
        <strong>SOAGENT.ORG</strong>
        <span>EARN INFINITY</span>
      </div>
    </div>
  );

  return (
    <div className="agent-portal-shell" data-ui="studio" data-theme={theme} data-section={activeSection} data-testid="agent-portal-page">
      <a href="#agent-main" className="agent-skip-link">Skip to content</a>

      <aside className="agent-sidebar">
        <div className="agent-sidebar-brand">
          <span className="agent-sidebar-mark"><img src="/assets/so-agents/brand-mark.png" alt="" /></span>
          <div className="agent-sidebar-brand-copy">
            <p><strong>SO</strong> Agents</p>
            <h2>Welcome, {agentName}</h2>
            <span><CalendarDays /> Partner since {partnerSince}</span>
          </div>
        </div>

        <button type="button" className="agent-sidebar-identity" onClick={() => setSection('profile')} aria-label="Open agent profile">
          <AgentWelcomeAvatar agent={agent} name={agentName} />
          <span><strong>{agentName}</strong><small>Independent partner</small></span>
          <ChevronRight />
        </button>

        <nav className="agent-sidebar-nav" aria-label="Agent portal sections">
          <div className="agent-sidebar-group-label">Workspace</div>
          {primarySections.map((section) => (
            <SidebarItem
              key={section.id}
              section={section}
              active={activeSection === section.id}
              onClick={() => setSection(section.id)}
            />
          ))}
          <div className="agent-sidebar-group-label">Business</div>
          {desktopBusinessSections.map((section) => (
            <SidebarItem
              key={section.id}
              section={section}
              active={activeSection === section.id}
              onClick={() => setSection(section.id)}
            />
          ))}
        </nav>

        <div className="agent-sidebar-footer">
          {moreSection && (
            <SidebarItem
              section={moreSection}
              active={moreSidebarActive}
              onClick={() => setSection('more')}
            />
          )}
          <button type="button" className="agent-theme-control" onClick={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
            <span>{theme === 'dark' ? <Moon /> : <Sun />}</span>
            <span className="agent-theme-copy">
              <strong>{theme === 'dark' ? 'Dark theme' : 'Light theme'}</strong>
              <small>Tap to switch appearance</small>
            </span>
          </button>
          <button type="button" className="agent-sidebar-support-card" onClick={() => setSection('team-chat')}>
            <span><MessageSquareText /></span>
            <span><strong>Partner support</strong><small>Open your team chat</small></span>
            <ChevronRight />
          </button>
        </div>
      </aside>

      <div className="agent-workspace">
        <header className="agent-topbar">
          <div className="agent-topbar-title">
            {mobileBackAvailable && (
              <button type="button" onClick={() => setSection('more')} className="agent-icon-button agent-mobile-back-button" aria-label="Back to More">
                <ArrowLeft />
              </button>
            )}
            <div>
              {activeSection === 'overview' ? (
                <>
                  <p className="agent-mobile-welcome">Partner Portal</p>
                  <h1>Home</h1>
                </>
              ) : (
                <>
                  <h1>{activeMeta.label}</h1>
                  <p>{activeMeta.description}</p>
                </>
              )}
            </div>
          </div>
          {(activeSection === 'chat' || activeSection === 'team-chat') && (
            <div className="agent-chat-mode-switch" role="group" aria-label="Chat type">
              <button type="button" aria-pressed={activeSection === 'chat'} className={activeSection === 'chat' ? 'is-active' : ''} onClick={() => setSection('chat')}><Users /> Customers <UnreadBadge count={chatUnreadCounts.customers} label="unread customer messages" /></button>
              <button type="button" aria-pressed={activeSection === 'team-chat'} className={activeSection === 'team-chat' ? 'is-active' : ''} onClick={() => setSection('team-chat')}><MessageSquareText /> Team <UnreadBadge count={chatUnreadCounts.team} label="unread team messages" /></button>
            </div>
          )}
          <form className="agent-global-search" onSubmit={submitPortalSearch} role="search">
            <Search />
            <input value={portalSearch} onChange={(event) => setPortalSearch(event.target.value)} placeholder="Search portal..." aria-label="Search portal sections" />
          </form>
          <div className="agent-topbar-actions">
            <button type="button" className="agent-icon-button" onClick={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
              {theme === 'dark' ? <Sun /> : <Moon />}
            </button>
            <button type="button" className="agent-icon-button" onClick={fetchData} aria-label="Refresh agent portal">
              <RefreshCw />
            </button>
            <div className="agent-notification-control">
              <button type="button" className="agent-icon-button" onClick={() => setNotificationsOpen((open) => !open)} aria-label="Notifications" aria-expanded={notificationsOpen}>
                <Bell />
                {notificationItems.length > 0 && <i />}
              </button>
              {notificationsOpen && (
                <div className="agent-notification-popover">
                  <div><strong>Notifications</strong><span>{notificationItems.length} recent</span></div>
                  {notificationItems.length ? notificationItems.map((item) => (
                    <button key={item.id} type="button" onClick={() => setSection(item.id.startsWith('withdrawal-') ? 'withdrawals' : 'ledger')}>
                      <span>{item.label}</span><small>{item.detail}</small>
                    </button>
                  )) : <p>No recent activity yet.</p>}
                </div>
              )}
            </div>
            <button type="button" onClick={() => setSection('profile')} className="agent-topbar-profile" aria-label="Open agent profile">
              <AgentWelcomeAvatar agent={agent} name={agentName} />
              <span><strong>{agentName}</strong><small>Partner since {partnerSince}</small></span>
            </button>
          </div>
        </header>

        <main
          ref={agentMainRef}
          id="agent-main"
          tabIndex={0}
          aria-label="Agent portal content"
          className={`agent-main ${activeSection === 'chat' ? 'agent-main-chat' : ''} ${pullRefreshStatus !== 'idle' ? 'has-pull-refresh' : ''}`}
          data-pull-refresh={pullRefreshStatus}
          style={{
            '--agent-pull-distance': `${pullDistance}px`,
            '--agent-pull-progress': pullRefreshProgress,
            '--agent-pull-remainder': `${100 - (pullRefreshProgress * 100)}%`,
            '--agent-pull-scale': 0.82 + (pullRefreshProgress * 0.18),
          }}
          onTouchStart={handlePullStart}
          onTouchMove={handlePullMove}
          onTouchEnd={handlePullEnd}
          onTouchCancel={resetPullRefresh}
        >
          <div className={`agent-pull-refresh-indicator is-${pullRefreshStatus}`} role="status" aria-live="polite" aria-hidden={pullRefreshStatus === 'idle'}>
            <span className="agent-pull-refresh-mark" aria-hidden="true">
              <img className="agent-pull-refresh-mark-base" src="/assets/so-agents/pull-refresh-mark.png" alt="" />
              <img className="agent-pull-refresh-mark-glow" src="/assets/so-agents/pull-refresh-mark.png" alt="" />
            </span>
            <span>{pullRefreshLabel}</span>
          </div>
          <div className={`agent-main-inner ${activeSection === 'chat' || activeSection === 'team-chat' ? 'is-chat' : ''}`}>
            {renderSection()}
          </div>
        </main>

        <nav className="agent-mobile-tabs" aria-label="Mobile agent portal sections">
          {mobileSections.map((section) => {
            const active = section.id === 'more' ? mobileMoreActive : activeSection === section.id;
            const NavIcon = AGENT_MOBILE_ICON_BY_ID[section.id] || section.icon;
            const chatUnreadTotal = Math.max(0, Number(chatUnreadCounts.customers) || 0)
              + Math.max(0, Number(chatUnreadCounts.team) || 0);
            return (
              <button
                key={section.id}
                type="button"
                data-tone={section.tone || 'green'}
                className={active ? 'is-active' : ''}
                onClick={() => setSection(section.id)}
                aria-current={active ? 'page' : undefined}
                aria-label={section.id === 'chat' && chatUnreadTotal > 0 ? `Chat, ${chatUnreadTotal} unread message${chatUnreadTotal === 1 ? '' : 's'}` : undefined}
              >
                <span className={`agent-concept-nav-icon agent-concept-nav-icon--${section.id}`} aria-hidden="true"><NavIcon /></span>
                {section.id === 'chat' && (
                  <UnreadBadge count={chatUnreadTotal} label="unread chat messages" className="agent-mobile-nav-unread" />
                )}
                <span>{section.id === 'chat' ? 'Chat' : section.label.replace('Referral ', '')}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {withdrawOpen && (
        <div className="agent-dialog-backdrop agent-v2-withdraw-backdrop" onClick={closeWithdrawal}>
          <section ref={withdrawDialogRef} className="agent-dialog agent-v2-withdraw-dialog" role="dialog" aria-modal="true" aria-labelledby="withdraw-title" aria-describedby="withdraw-description" onClick={(event) => event.stopPropagation()}>
            <div className="agent-v2-withdraw-intro">
              <div className="agent-v2-withdraw-intro-copy">
                <span className="agent-v2-kicker"><ShieldCheck /> Secure payout request</span>
                <h2 ref={withdrawHeadingRef} tabIndex={-1} id="withdraw-title">{WITHDRAWAL_STEP_COPY[currentWithdrawalStep.id].title}</h2>
                <p id="withdraw-description">{WITHDRAWAL_STEP_COPY[currentWithdrawalStep.id].description}</p>
              </div>
              <img className="agent-v2-withdraw-intro-art" src={currentWithdrawalStep.icon} width="320" height="320" decoding="async" alt="" aria-hidden="true" />
              <button type="button" className="agent-icon-button" onClick={closeWithdrawal} aria-label="Close withdrawal form" disabled={withdrawSubmitting}>
                <X />
              </button>
            </div>

            <ol className="agent-v2-withdraw-steps" aria-label="Withdrawal progress">
              {WITHDRAWAL_STEPS.map((step, index) => (
                <li
                  key={step.id}
                  aria-current={withdrawStep === step.id ? 'step' : undefined}
                  className={withdrawStep === step.id ? 'is-active' : index < currentWithdrawalStepIndex ? 'is-complete' : ''}
                >
                  <span><img src={step.icon} alt="" aria-hidden="true" /><b>{index + 1}</b></span>
                  <div><strong>{step.label}</strong><small>{step.short}</small></div>
                </li>
              ))}
            </ol>

            <div className="agent-v2-withdraw-stage-wrap">
              {withdrawStep === 'amount' && (
                <form className="agent-v2-withdraw-form agent-v2-withdraw-stage" onSubmit={(event) => { event.preventDefault(); continueWithdrawalAmount(); }}>
                  <div className="agent-v2-stage-heading">
                    <img src={withdrawalAmountStep} alt="" aria-hidden="true" />
                    <div><span>Step 1 of 4</span><h3>Enter withdrawal amount</h3><p>Your current available balance is {fmtUSD(analytics?.wallet?.available_usd ?? summary.balance_usd)}.</p></div>
                  </div>
                  <label className="agent-v2-field agent-v2-amount-field agent-v2-amount-field-large">
                    <span>Amount in USD</span>
                    <div><b>$</b><Input type="number" min="1" max={Number(analytics?.wallet?.available_usd ?? summary.balance_usd ?? 0) || undefined} inputMode="decimal" value={withdrawForm.amount_usd} onChange={(event) => setWithdrawForm({ ...withdrawForm, amount_usd: event.target.value })} autoFocus placeholder="0.00" /></div>
                    <small>Available to withdraw: {fmtUSD(analytics?.wallet?.available_usd ?? summary.balance_usd)}</small>
                  </label>
                  <div className="agent-v2-dialog-actions">
                    <button type="button" className="agent-v2-button-secondary" onClick={closeWithdrawal}>Cancel</button>
                    <Button type="submit" className="agent-primary-button">Choose payout method <ChevronRight /></Button>
                  </div>
                </form>
              )}

              {withdrawStep === 'method' && (
                <form className="agent-v2-withdraw-form agent-v2-withdraw-stage" onSubmit={(event) => { event.preventDefault(); continueWithdrawalMethod(); }}>
                  <div className="agent-v2-stage-heading">
                    <img src={withdrawalMethodStep} alt="" aria-hidden="true" />
                    <div><span>Step 2 of 4</span><h3>Select payout method</h3><p>All methods available to your agent account are listed below.</p></div>
                  </div>
                  <label className="agent-v2-field agent-v2-method-field">
                    <span>Payout method</span>
                    <div className="agent-v2-method-select-shell">
                      <img src={withdrawalMethodIcon(selectedWithdrawalMethod)} alt="" aria-hidden="true" />
                      <select value={withdrawForm.method} onChange={(event) => setWithdrawForm({ ...withdrawForm, method: event.target.value })}>
                        {methods.map((method) => {
                          const id = withdrawalMethodId(method);
                          return <option key={id} value={id}>{withdrawalMethodLabel(method)}</option>;
                        })}
                      </select>
                    </div>
                    {['bank_deposit', 'cash_drop'].includes(withdrawForm.method)
                      ? <small className="agent-v2-method-limit">This method is available only above NPR {withdrawalHighValueMinimumNpr.toLocaleString()}, using the current portal conversion rate.</small>
                      : <small>You can review the selected destination before submitting.</small>}
                  </label>
                  <div className="agent-v2-method-grid" aria-label="Available payout methods">
                    {methods.map((method) => {
                      const id = withdrawalMethodId(method);
                      const active = id === withdrawForm.method;
                      return (
                        <button key={id} type="button" className={active ? 'is-active' : ''} onClick={() => setWithdrawForm({ ...withdrawForm, method: id })}>
                          <img src={withdrawalMethodIcon(method)} alt="" aria-hidden="true" />
                          <span>{withdrawalMethodLabel(method)}</span>
                          {active && <CheckCircle2 aria-hidden="true" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="agent-v2-dialog-actions">
                    <button type="button" className="agent-v2-button-secondary" onClick={() => setWithdrawStep('amount')}><ArrowLeft /> Back</button>
                    <Button type="submit" className="agent-primary-button">Add payout details <ChevronRight /></Button>
                  </div>
                </form>
              )}

              {withdrawStep === 'details' && (
                <div className="agent-form-stack agent-v2-withdraw-form agent-v2-withdraw-stage">
                  <div className="agent-v2-stage-heading">
                    <img src={withdrawalDetailsStep} alt="" aria-hidden="true" />
                    <div><span>Step 3 of 4</span><h3>Photo and payout note</h3><p>Add the destination details clearly so the payout team can process your request correctly.</p></div>
                  </div>
                  <div className="agent-v2-details-grid">
                    <label className="agent-v2-field">
                      <span>Payout photo</span>
                      <div className={`agent-v2-proof-upload ${withdrawForm.photo_url ? 'has-photo' : ''}`}>
                        {!withdrawForm.photo_url && <img src={payoutProofCaptureSpot} width="480" height="480" loading="lazy" decoding="async" alt="" aria-hidden="true" />}
                        <div>
                          <input ref={withdrawPhotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadWithdrawalPhoto} className="sr-only" tabIndex={-1} aria-label="Upload payout photo" />
                          <div className="agent-withdraw-photo-row">
                            <button type="button" className="agent-profile-upload-button" onClick={() => withdrawPhotoInputRef.current?.click()} disabled={withdrawPhotoUploading}>
                              <Camera />
                              <span>{withdrawPhotoUploading ? 'Uploading…' : withdrawForm.photo_url ? 'Change photo' : 'Add photo'}</span>
                            </button>
                            {withdrawForm.photo_url && <a href={withdrawForm.photo_url} target="_blank" rel="noreferrer">View photo</a>}
                          </div>
                          <small>Optional proof. JPG, PNG, or WEBP; maximum 5 MB.</small>
                        </div>
                      </div>
                    </label>
                    <label className="agent-v2-field">
                      <span>Complete payout note</span>
                      <Textarea value={withdrawForm.note} maxLength={2000} onChange={(event) => setWithdrawForm({ ...withdrawForm, note: event.target.value })} rows={7} className="agent-withdraw-note" placeholder="Add account details, wallet address, bank information, or instructions for the payout team…" />
                      <small>{withdrawForm.note.length}/2000 characters</small>
                    </label>
                  </div>
                  <div className="agent-v2-dialog-actions">
                    <button type="button" className="agent-v2-button-secondary" onClick={() => setWithdrawStep('method')}><ArrowLeft /> Back</button>
                    <Button onClick={reviewWithdrawal} className="agent-primary-button">Review withdrawal <ChevronRight /></Button>
                  </div>
                </div>
              )}

              {withdrawStep === 'review' && (
                <div className="agent-v2-withdraw-review agent-v2-withdraw-stage">
                  <div className="agent-v2-stage-heading">
                    <img src={withdrawalReviewStep} alt="" aria-hidden="true" />
                    <div><span>Step 4 of 4</span><h3>Final review</h3><p>Confirm the amount, method, photo, and note before sending.</p></div>
                  </div>
                <div className="agent-v2-review-amount"><span>Amount requested</span><strong>{fmtUSD(withdrawForm.amount_usd)}</strong><small>{Number(analytics?.fee_rules?.npr_per_usd || 0) > 0 ? `Approximately NPR ${(Number(withdrawForm.amount_usd || 0) * Number(analytics.fee_rules.npr_per_usd)).toLocaleString(undefined, { maximumFractionDigits: 2 })} at the current portal rate` : 'Reserved only after you submit'}</small></div>
                <dl>
                  <div><dt>Method</dt><dd>{withdrawalMethodLabel(selectedWithdrawalMethod) || withdrawForm.method}</dd></div>
                  <div><dt>Photo</dt><dd>{withdrawForm.photo_url ? 'Attached' : 'Not attached'}</dd></div>
                  <div><dt>Payout note</dt><dd>{withdrawForm.note.trim() || 'No note added'}</dd></div>
                  <div><dt>Available after request</dt><dd>{fmtUSD(Math.max(0, Number(analytics?.wallet?.available_usd ?? summary.balance_usd ?? 0) - Number(withdrawForm.amount_usd || 0)))}</dd></div>
                </dl>
                <div className="agent-v2-review-notice"><ShieldCheck /><p><strong>Final check</strong><span>The payout team will review this request. Its status and audit history will stay visible in Withdrawals.</span></p></div>
                <div className="agent-v2-dialog-actions">
                  <button type="button" className="agent-v2-button-secondary" onClick={() => setWithdrawStep('details')} disabled={withdrawSubmitting}><ArrowLeft /> Edit details</button>
                  <Button onClick={submitWithdrawal} disabled={withdrawSubmitting} className="agent-primary-button">{withdrawSubmitting ? 'Sending request…' : `Request ${fmtUSD(withdrawForm.amount_usd)} withdrawal`}</Button>
                </div>
              </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function SidebarItem({ section, active, onClick }) {
  const Icon = section.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`agent-sidebar-item ${active ? 'is-active' : ''}`}
      data-tone={section.tone || 'green'}
      data-testid={`agent-nav-${section.id}`}
      aria-current={active ? 'page' : undefined}
    >
      <Icon />
      <span>{section.label}</span>
    </button>
  );
}

function SectionTitle({ eyebrow, title, description, action }) {
  return (
    <div className="agent-section-title agent-v2-page-head">
      <div className="agent-v2-page-head-copy">
        {eyebrow && <p className="agent-v2-page-eyebrow"><span />{eyebrow}</p>}
        <h2 className="agent-v2-page-title">{title}</h2>
        {description && <span className="agent-v2-page-description">{description}</span>}
      </div>
      {action && <div className="agent-section-action agent-v2-page-actions">{action}</div>}
    </div>
  );
}

function Panel({ children, className = '' }) {
  return (
    <section className={`agent-panel agent-v2-surface ${className}`}>
      {children}
    </section>
  );
}

function SectionVisual({ image, eyebrow, title, description, action, asHeading = false, className = '' }) {
  const TitleTag = asHeading ? 'h2' : 'h3';
  return (
    <Panel className={`agent-v2-visual-strip ${className}`}>
      <div className="agent-v2-visual-copy">
        {eyebrow && <span>{eyebrow}</span>}
        <TitleTag>{title}</TitleTag>
        {description && <p>{description}</p>}
        {action && <div className="agent-v2-visual-action">{action}</div>}
      </div>
      <div className="agent-v2-visual-art">
        <img src={image} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" />
      </div>
    </Panel>
  );
}

function EmptyState({ children, title, icon: Icon, action }) {
  return (
    <div className="agent-empty-state agent-v2-empty-state">
      {Icon && <span className="agent-v2-empty-icon"><Icon /></span>}
      {title && <strong>{title}</strong>}
      <p>{children}</p>
      {action}
    </div>
  );
}

function UnreadBadge({ count, label, className = '' }) {
  const unread = Math.max(0, Number(count) || 0);
  if (!unread) return null;
  return <span className={`agent-unread-badge ${className}`.trim()} aria-label={`${unread} ${label}`}>{unread > 99 ? '99+' : unread}</span>;
}

function AgentChatMobileModeSwitch({ active, onSection, unreadCounts = {} }) {
  return (
    <div className="agent-chat-mobile-mode-switch" role="group" aria-label="Chat type">
      <button type="button" aria-pressed={active === 'customers'} className={active === 'customers' ? 'is-active' : ''} onClick={() => onSection('chat')}>
        <Users />
        <span>Customers</span>
        <UnreadBadge count={unreadCounts.customers} label="unread customer messages" />
      </button>
      <button type="button" aria-pressed={active === 'team'} className={active === 'team' ? 'is-active' : ''} onClick={() => onSection('team-chat')}>
        <MessageSquareText />
        <span>Team</span>
        <UnreadBadge count={unreadCounts.team} label="unread team messages" />
      </button>
    </div>
  );
}

function AgentChatSection({ adminPreview, onSection, unreadCounts, onUnreadCountChange }) {
  return (
    <section className="agent-customer-chat-section" data-testid="agent-live-chat-section">
      {/* The portal top bar already names this page, so the inbox goes
          straight to the conversations and keeps the vertical space. */}
      <AgentChatMobileModeSwitch active="customers" onSection={onSection} unreadCounts={unreadCounts} />
      {adminPreview && (
        <div className="mb-3 shrink-0">
          <p className="agent-chat-description">
            Admin preview shows live chat in the portal. Agent accounts should only work with players attributed to their links.
          </p>
        </div>
      )}
      <div className="agent-chat-embed-frame min-h-0 flex-1">
        <ChatSupportPage embedded agentPortal onUnreadCountChange={onUnreadCountChange} />
      </div>
    </section>
  );
}

function MoreSection({ agent, agentName, theme, sections, onSection, onThemeChange, onLogout }) {
  const groups = [
    { label: 'Growth', ids: ['leads', 'invite-agent', 'links', 'referrals'] },
    { label: 'Money', ids: ['earnings', 'withdrawals', 'ledger'] },
    { label: 'Insights', ids: ['reports', 'analytics', 'moneyflow', 'fees'] },
    { label: 'Operations', ids: ['games'] },
    { label: 'Support & Resources', ids: ['team-chat', 'how'] },
    { label: 'Account', ids: ['profile'] },
  ];
  const sectionById = new Map(sections.map((section) => [section.id, section]));

  return (
    <div className="agent-more-page">
      <div className="agent-mobile-page-title">
        <div><h1>More</h1><p>All your tools and settings</p></div>
        <Bell />
      </div>
      <button type="button" className="agent-more-profile-card" onClick={() => onSection('profile')}>
        <AgentWelcomeAvatar agent={agent} name={agentName} />
        <span><strong>{agentName}</strong><small>Partner since {fmtShortDate(agent.created_at)}</small><b>View profile</b></span>
        <ShieldCheck />
      </button>

      {groups.map((group) => (
        <section key={group.label} className="agent-more-group">
          <h2>{group.label}</h2>
          <div>
            {group.ids.map((id) => {
              const section = sectionById.get(id);
              if (!section) return null;
              const Icon = section.icon;
              return (
                <button key={id} type="button" onClick={() => onSection(id)}>
                  <IconTile icon={Icon} tone={section.tone || 'green'} />
                  <span><strong>{section.label}</strong><small>{section.description}</small></span>
                  <ChevronRight />
                </button>
              );
            })}
            {group.label === 'Account' && (
              <>
                <button type="button" onClick={onThemeChange}>
                  <span>{theme === 'dark' ? <Moon /> : <Sun />}</span>
                  <span><strong>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</strong><small>Tap to change appearance</small></span>
                  <ChevronRight />
                </button>
                <button type="button" onClick={onLogout}>
                  <span><LogOut /></span>
                  <span><strong>Log out</strong><small>End this secure session</small></span>
                  <ChevronRight />
                </button>
              </>
            )}
          </div>
        </section>
      ))}
      <p className="agent-more-level">Partner since {fmtShortDate(agent.created_at)}</p>
    </div>
  );
}

function LeadCenterSection() {
  const pipeline = [
    { label: 'New', description: 'Fresh survey responses', tone: 'blue' },
    { label: 'Follow-up', description: 'Ready for your next contact', tone: 'amber' },
    { label: 'Interested', description: 'Qualified prospects', tone: 'violet' },
    { label: 'Converted', description: 'Joined customers', tone: 'green' },
  ];

  return (
    <div className="agent-lead-center" data-testid="agent-lead-center">
      <section className="agent-lead-center-hero">
        <IconTile icon={LeadCenterIcon} tone="violet" />
        <div>
          <p>Growth workspace</p>
          <h2>Lead Center</h2>
          <span>Campaign and survey leads will appear here for agent follow-up.</span>
        </div>
        <em>Ready</em>
      </section>

      <section className="agent-lead-pipeline" aria-label="Lead pipeline">
        {pipeline.map((stage) => (
          <article key={stage.label} data-tone={stage.tone}>
            <span>{stage.label}</span>
            <strong>0</strong>
            <small>{stage.description}</small>
          </article>
        ))}
      </section>

      <Panel className="agent-lead-empty-panel">
        <span className="agent-lead-empty-icon"><LeadCenterIcon /></span>
        <div>
          <h3>No leads yet</h3>
          <p>New SO Agents campaign survey submissions will be collected here once the lead intake is connected.</p>
        </div>
        <ul>
          <li>Survey source and campaign</li>
          <li>Assigned agent and follow-up status</li>
          <li>Contact history and conversion record</li>
        </ul>
      </Panel>
    </div>
  );
}

function ProfileSection({ agent, adminPreview, onSaved }) {
  const initialProfile = useMemo(() => {
    const profile = agent?.agent_chat_profile || {};
    return {
      display_name: profile.display_name || agent?.display_name || agent?.full_name || agent?.username || '',
      username: agent?.username || '',
      title: profile.title || 'Live Agent',
      avatar_url: profile.avatar_url || '',
      bio: profile.bio || '',
    };
  }, [agent]);
  const [form, setForm] = useState(initialProfile);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    setForm(initialProfile);
  }, [initialProfile]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || adminPreview) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choose a JPG, PNG, or WEBP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Profile photo must be 5 MB or smaller');
      return;
    }

    const payload = new FormData();
    payload.append('file', file);
    try {
      setUploadingPhoto(true);
      const response = await api.post('/api/agent-earn/me/profile/avatar', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const profile = response.data?.agent?.agent_chat_profile || response.data?.agent_chat_profile || {};
      const avatarUrl = response.data?.avatar_url || profile.avatar_url;
      if (!avatarUrl) throw new Error('Profile photo URL was not returned');
      setForm((current) => ({ ...current, ...profile, avatar_url: avatarUrl }));
      toast.success('Profile photo updated');
      await onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Profile photo could not be uploaded');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveProfile = async () => {
    if (adminPreview) return;
    try {
      setSaving(true);
      await api.patch('/api/agent-earn/me/profile', {
        display_name: form.display_name,
        username: form.username,
        title: form.title,
        avatar_url: form.avatar_url,
        bio: form.bio,
      });
      toast.success('Profile updated');
      await onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Profile could not be updated');
    } finally {
      setSaving(false);
    }
  };

  const previewInitial = (form.display_name || form.username || 'A').charAt(0).toUpperCase();

  return (
    <div className="agent-page agent-v2-profile-page space-y-5">
      <SectionTitle
        eyebrow="Agent identity"
        title="Profile"
        description="This is the name shown in your portal, customer chat, and team conversations."
        action={
          <Button onClick={saveProfile} disabled={saving || adminPreview} className="bg-[#D4AF37] text-black font-black">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        }
      />

      {adminPreview && (
        <Panel className="p-4 border-[#D4AF37]/25 bg-[#D4AF37]/10">
          <p className="text-sm font-bold text-[#FDE68A]">Admin preview mode</p>
          <p className="mt-1 text-xs text-gray-300">Sign in as an agent to update that agent's live chat profile.</p>
        </Panel>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[0.75fr_1.25fr] gap-4">
        <Panel className="p-5">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-black">Public preview</p>
          <div className="agent-profile-preview">
            <div className="agent-profile-preview-body">
              <div className="agent-profile-avatar-wrap">
                <button
                  type="button"
                  className="agent-profile-avatar-button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={adminPreview || uploadingPhoto}
                  aria-label="Upload profile photo"
                >
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="Agent profile" />
                  ) : (
                    <span>{previewInitial}</span>
                  )}
                  <span className="agent-profile-avatar-camera"><Camera /></span>
                </button>
                <span className="agent-profile-avatar-status" />
              </div>
              <div className="agent-profile-preview-copy">
                <h3>{form.display_name || form.username || agent?.display_name || agent?.full_name || agent?.username || 'Agent'}</h3>
                <p>{form.title || 'Live Agent'}</p>
                <span>{form.bio || 'Add a short bio so players know who they are chatting with.'}</span>
              </div>
            </div>
            <div className="agent-profile-photo-control">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={uploadPhoto}
                disabled={adminPreview || uploadingPhoto}
                className="sr-only"
                tabIndex={-1}
                aria-label="Upload profile photo"
              />
              <button
                type="button"
                className="agent-profile-upload-button"
                onClick={() => photoInputRef.current?.click()}
                disabled={adminPreview || uploadingPhoto}
              >
                <Camera />
                <span>{uploadingPhoto ? 'Uploading...' : form.avatar_url ? 'Change Photo' : 'Upload Photo'}</span>
              </button>
              <small>JPG, PNG, or WEBP. Maximum 5 MB.</small>
            </div>
          </div>
        </Panel>

        <Panel className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="agent-profile-field">
              <span>Name shown to players</span>
              <Input
                value={form.display_name}
                disabled={adminPreview}
                maxLength={80}
                onChange={(e) => setField('display_name', e.target.value)}
                className="agent-profile-input"
                placeholder="Your public name"
              />
            </label>
            <label className="agent-profile-field">
              <span>Username</span>
              <Input
                value={form.username}
                disabled={adminPreview}
                maxLength={32}
                onChange={(e) => setField('username', e.target.value)}
                className="agent-profile-input"
                placeholder="Your sign-in username"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </label>
            <label className="agent-profile-field">
              <span>Chat title</span>
              <Input
                value={form.title}
                disabled={adminPreview}
                maxLength={80}
                onChange={(e) => setField('title', e.target.value)}
                className="agent-profile-input"
                placeholder="Live Agent"
              />
            </label>
          </div>
          <label className="agent-profile-field mt-4">
            <span>Bio</span>
            <Textarea
              value={form.bio}
              disabled={adminPreview}
              maxLength={500}
              rows={6}
              onChange={(e) => setField('bio', e.target.value)}
              className="agent-profile-input"
              placeholder="Write a short player-facing intro."
            />
            <small>{form.bio.length}/500</small>
          </label>
        </Panel>
      </div>
    </div>
  );
}

function OverviewSection({ agent, summary, verificationSummary, reportSummary, players, assignments, ledger, withdrawals, analytics, monthEarnings: monthEarningsReport, adminPreview, theme, onThemeChange, onSection, onWithdraw, onProfileSaved, onCopy }) {
  const [moneyVisible, setMoneyVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('agent-portal-money-visible') !== 'false';
  });
  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const profitSeries = useMemo(() => getDailyEarningSeries(analytics, 30), [analytics]);
  const recentLedger = ledger.slice(0, 7);
  const recentWithdrawals = withdrawals.slice(0, 5);
  const now = new Date();
  const firstName = String(agent?.display_name || agent?.agent_chat_profile?.display_name || agent?.full_name || agent?.username || 'Agent').trim().split(/\s+/)[0];
  const greeting = greetingFor(now);
  const todayLabel = fmtLongDay(now);
  const newCustomersThisMonth = players.filter((player) => {
    const createdAt = new Date(player.created_at);
    return !Number.isNaN(createdAt.getTime()) && createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
  }).length;
  const activeCustomers = players.filter((player) => player.active !== false && !player.disabled).length;
  const inactiveCustomers = Math.max(Number(summary.customers_total || players.length) - activeCustomers, 0);
  const attentionCustomers = players.filter((player) => Number(player.net_revenue_usd || 0) < 0).length;
  const approvedWithdrawals = withdrawals.filter((row) => ['approved', 'completed', 'paid'].includes(String(row.status || '').toLowerCase()));
  const pendingWithdrawals = withdrawals.filter((row) => !['approved', 'completed', 'paid', 'rejected', 'failed'].includes(String(row.status || '').toLowerCase()));
  const approvedWithdrawalTotal = approvedWithdrawals.reduce((sum, row) => sum + Number(row.amount_usd || 0), 0);
  const pendingWithdrawalTotal = pendingWithdrawals.reduce((sum, row) => sum + Number(row.amount_usd || 0), 0);
  const previousProfit = Number(profitSeries.at(-2)?.profit || 0);
  const currentProfit = Number(profitSeries.at(-1)?.profit || 0);
  const profitChange = previousProfit === 0 ? 0 : ((currentProfit - previousProfit) / Math.abs(previousProfit)) * 100;
  const customerStatus = [
    { name: 'Active', value: activeCustomers, fill: '#16a34a' },
    { name: 'Attention', value: attentionCustomers, fill: '#f59e0b' },
    { name: 'Inactive', value: inactiveCustomers, fill: '#ef4444' },
  ].filter((item) => item.value > 0);
  const projectedMonthEarnings = (analytics?.daily || []).filter((row) => {
    const date = new Date(`${row.day}T00:00:00Z`);
    return date.getUTCMonth() === now.getUTCMonth() && date.getUTCFullYear() === now.getUTCFullYear();
  }).reduce((sum, row) => sum + Number(row.earned_usd || 0), 0);
  const monthEarnings = Number(monthEarningsReport?.total?.net_earned_usd ?? projectedMonthEarnings);
  const nprPerUsd = (() => {
    const configuredRate = Number(analytics?.fee_rules?.npr_per_usd);
    return Number.isFinite(configuredRate) && configuredRate > 0 ? configuredRate : 133;
  })();
  const availableUsd = Number(analytics?.wallet?.available_usd ?? summary.balance_usd ?? 0);
  const availableNpr = Math.round(availableUsd * nprPerUsd).toLocaleString('en-US');
  const gameCreditsActivityUsd = (analytics?.daily || []).filter((row) => {
    const date = new Date(`${row.day}T00:00:00Z`);
    return date.getUTCMonth() === now.getUTCMonth() && date.getUTCFullYear() === now.getUTCFullYear();
  }).reduce((sum, row) => sum + Number(row.net_house_fee_usd || 0), 0);
  const agentName = agent?.display_name || agent?.agent_chat_profile?.display_name || agent?.full_name || agent?.username || agent?.public_identity || 'Agent';
  const referralAssignment = assignments.find((assignment) => assignment.active !== false) || assignments[0] || null;
  const referralLink = referralAssignment?.tracking_url || referralAssignment?.tracking_code || '';
  const agentConnectionCode = referralAssignment?.tracking_code || '';
  const playerPromoCode = referralAssignment?.player_promo?.code || '';
  const referralSiteName = referralAssignment?.tenant?.name || 'Customer';
  const recentActivity = [
    ...ledger.filter((row) => !WITHDRAWAL_BOOKKEEPING_REASONS.has(String(row.reason || '').toUpperCase())).map((row) => {
      const credit = String(row.direction || '').toUpperCase() === 'CREDIT';
      const amount = Math.abs(Number(row.amount_usd ?? row.amount ?? 0));
      return { id: `ledger-${row.id}`, title: formatLedgerEventLabel(row.reason || row.event_type), subtitle: row.description || fmtDate(row.created_at), amount: credit ? amount : -amount, created_at: row.created_at, kind: credit ? 'in' : 'out' };
    }),
    ...withdrawals.map((row) => {
      const status = String(row.status || 'pending').toLowerCase();
      const settled = ['approved', 'completed', 'paid'].includes(status);
      return {
        id: `withdrawal-${row.id}`,
        title: settled ? 'Withdrawal completed' : 'Withdrawal request',
        subtitle: status,
        amount: settled ? -Math.abs(Number(row.amount_usd || 0)) : 0,
        created_at: row.created_at,
        kind: 'out',
      };
    }),
  ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 4);
  const quickActions = [
    { id: 'links', label: 'Share referral link', helper: `${assignments.length || 0} available`, icon: Link2 },
    { id: 'players', label: 'My customers', helper: `${summary.customers_total || 0} attributed`, icon: CustomersGroupIcon },
    { id: 'chat', label: 'Customer chat', helper: 'Customer inbox', icon: MessageCircle },
    { id: 'team-chat', label: 'Team chat', helper: 'Internal support', icon: MessageSquareText },
    { id: 'reports', label: 'View reports', helper: `${fmtPct(reportSummary.in_percent)} in flow`, icon: ReportsSectionIcon },
    { id: 'withdrawals', label: 'Withdraw now', helper: fmtUSD(summary.balance_usd), icon: WithdrawActionIcon, onClick: onWithdraw },
  ];
  const toggleMoneyVisibility = () => {
    setMoneyVisible((visible) => {
      const next = !visible;
      window.localStorage.setItem('agent-portal-money-visible', String(next));
      return next;
    });
  };

  const uploadHomeAvatar = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || adminPreview) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choose a JPG, PNG, or WEBP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Profile photo must be 5 MB or smaller');
      return;
    }

    const payload = new FormData();
    payload.append('file', file);
    try {
      setAvatarUploading(true);
      const response = await api.post('/api/agent-earn/me/profile/avatar', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!response.data?.avatar_url && !response.data?.agent?.agent_chat_profile?.avatar_url) {
        throw new Error('Profile photo URL was not returned');
      }
      toast.success('Profile photo updated');
      await onProfileSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Profile photo could not be uploaded');
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <div className="agent-page agent-overview-page">
      {adminPreview && (
        <div className="agent-notice" role="status">
          <ShieldCheck />
          <div><strong>Read-only admin preview</strong><span>Live agent actions are disabled in this preview.</span></div>
        </div>
      )}

      <div className="agent-reference-home">
        <div className="agent-reference-mobile-home-header">
          <div className="agent-reference-home-identity">
            <div className="agent-reference-welcome-row">
              <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadHomeAvatar} disabled={adminPreview || avatarUploading} className="sr-only" tabIndex={-1} aria-label="Upload profile photo" />
              <AgentWelcomeAvatar agent={agent} name={agentName} onUpload={() => avatarInputRef.current?.click()} uploading={avatarUploading} disabled={adminPreview} />
              <div className="agent-reference-welcome-copy">
                <span className="agent-reference-home-date">{todayLabel}</span>
                <h1>{greeting}, {firstName}.</h1>
                <button type="button" onClick={() => onSection('profile')}><CalendarDays /> Partner since {fmtShortDate(agent.created_at)} <ChevronRight /></button>
              </div>
            </div>
          </div>
          <div className="agent-reference-home-tools">
            <button type="button" onClick={onThemeChange} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
              {theme === 'dark' ? <Sun /> : <Moon />}
            </button>
            <button type="button" onClick={() => onSection('ledger')} aria-label="View notifications"><Bell /><i /></button>
          </div>
        </div>
        <section className="agent-reference-home-main">
          <Panel className="agent-reference-earnings">
            <div className="agent-reference-earnings-primary">
              <div className="agent-reference-earnings-heading">
                <span>Earnings This Month</span>
                <button
                  type="button"
                  className="agent-money-visibility-toggle"
                  onClick={toggleMoneyVisibility}
                  aria-label={moneyVisible ? 'Hide money amounts' : 'Show money amounts'}
                  title={moneyVisible ? 'Hide money amounts' : 'Show money amounts'}
                >
                  {moneyVisible ? <Eye /> : <EyeOff />}
                </button>
              </div>
              <button type="button" className="agent-reference-earnings-copy" onClick={() => onSection('earnings')} aria-label="Open Your Earnings">
                <strong>{moneyVisible ? fmtUSD(monthEarnings) : '••••••'}</strong>
                <small>{moneyVisible ? `${fmtUSD(availableUsd)} available now · NPR ${availableNpr}` : 'Balance hidden'}</small>
              </button>
              <button type="button" className="agent-reference-withdraw-cta" onClick={onWithdraw}>Withdraw <ChevronRight /></button>
            </div>
            <button type="button" className="agent-reference-payout-shortcut" onClick={onWithdraw} aria-label="Open weekly payouts"><IconTile icon={WithdrawActionIcon} /><span>Weekly payouts</span><ChevronRight /></button>
          </Panel>
          <div className="agent-reference-stat-grid">
            <button type="button" onClick={() => onSection('players')}>
              <IconTile icon={CustomersGroupIcon} tone="green" />
              <strong>{Number(summary.customers_total || 0).toLocaleString()}</strong>
              <small>Customers</small>
              <em className="agent-reference-stat-support">Total customers</em>
              <ChevronRight />
            </button>
            <button type="button" onClick={() => onSection('players')}>
              <IconTile icon={NewJoinedIcon} tone="blue" />
              <strong className="agent-reference-positive-value">+{newCustomersThisMonth.toLocaleString()}</strong>
              <small>New Joined Customers</small>
              <em className="agent-reference-stat-support">This month</em>
              <ChevronRight />
            </button>
            <button type="button" onClick={() => onSection('games')}><IconTile icon={CreditsUsedIcon} tone="amber" /><strong>{moneyVisible ? fmtUSD(gameCreditsActivityUsd) : '••••'}</strong><small>Game Credits Activity</small><em className="agent-reference-stat-support">This month</em><ChevronRight /></button>
          </div>
          <Panel className="agent-reference-referral">
            <IconTile icon={ReferralLinkIcon} />
            <div>
              <strong>Your Customer Link</strong>
              <small>Share this link to register customers to your network.</small>
              {referralLink ? (
                referralAssignment?.tracking_url ? (
                  <a className="agent-reference-referral-link" href={referralAssignment.tracking_url} target="_blank" rel="noreferrer" title={referralLink}>{referralLink}</a>
                ) : (
                  <code className="agent-reference-referral-link" title={referralLink}>{referralLink}</code>
                )
              ) : <span className="agent-reference-referral-empty">No referral link assigned yet</span>}
              {(agentConnectionCode || playerPromoCode) && (
                <div className="agent-reference-code-row">
                  {agentConnectionCode && <button type="button" onClick={() => onCopy(agentConnectionCode, 'Agent connection code copied')}><span>Agent code</span><strong>{agentConnectionCode}</strong></button>}
                  {playerPromoCode && <button type="button" onClick={() => onCopy(playerPromoCode, 'Player promo code copied')}><span>Promo code</span><strong>{playerPromoCode}</strong></button>}
                </div>
              )}
            </div>
            <button type="button" aria-label={referralLink ? 'Copy customer link' : 'View customer links'} onClick={() => referralLink ? onCopy(referralLink, 'Customer link copied') : onSection('links')}><span>{referralLink ? 'Copy Link' : 'View Links'}</span><Copy /></button>
          </Panel>
          <Panel className="agent-reference-activity">
            <div className="agent-reference-card-heading"><h3>Recent Activity</h3><button type="button" onClick={() => onSection('ledger')}>View all</button></div>
            {recentActivity.length ? recentActivity.map((item) => (
              <button key={item.id} type="button" onClick={() => onSection(item.id.startsWith('withdrawal-') ? 'withdrawals' : 'ledger')}>
                <span className={item.kind === 'out' ? 'is-out' : 'is-in'}>{item.kind === 'out' ? <ArrowUpRight /> : <BadgeDollarSign />}</span>
                <span><strong>{item.title}</strong><small>{item.subtitle}</small><em>{fmtDate(item.created_at)}</em></span>
                <b className={item.amount < 0 ? 'is-out' : 'is-in'}>{moneyVisible ? (item.amount ? `${item.amount > 0 ? '+' : '-'}${fmtUSD(Math.abs(item.amount))}` : '—') : '••••'}</b>
                <ChevronRight />
              </button>
            )) : <EmptyState>No recent activity yet.</EmptyState>}
          </Panel>
        </section>
        <aside className="agent-reference-home-rail">
          <Panel className="agent-reference-rail-card">
            <div className="agent-reference-card-heading"><h3>Notifications</h3><button type="button" onClick={() => onSection('ledger')}>View all</button></div>
            {[...recentActivity].slice(0, 4).map((item) => <button key={`notice-${item.id}`} type="button" onClick={() => onSection(item.id.startsWith('withdrawal-') ? 'withdrawals' : 'ledger')}><span><Bell /></span><span><strong>{item.title}</strong><small>{fmtDate(item.created_at)}</small></span></button>)}
            {!recentActivity.length && <p>No recent notifications.</p>}
          </Panel>
          <Panel className="agent-reference-support">
            <div><LifeBuoy /><span><strong>Team Support</strong><small>We’re here to help.</small></span><i /></div>
            <button type="button" onClick={() => onSection('team-chat')}>Start Chat <Send /></button>
          </Panel>
          <Panel className="agent-reference-progress">
            <div className="agent-reference-card-heading"><h3>Your Progress</h3><span>This Month</span></div>
            <div><span><Users /> Customers Added <b>{newCustomersThisMonth}</b></span><i><em style={{ width: `${Math.min(100, Number(summary.customers_total || 0) ? (newCustomersThisMonth / Number(summary.customers_total || 1)) * 100 : 0)}%` }} /></i></div>
            <div><span><BadgeDollarSign /> Earnings <b>{moneyVisible ? fmtUSD(monthEarnings) : '••••'}</b></span><i><em style={{ width: `${Math.min(100, Number(analytics?.wallet?.lifetime_earned_usd || 0) ? (monthEarnings / Number(analytics.wallet.lifetime_earned_usd)) * 100 : 0)}%` }} /></i></div>
          </Panel>
        </aside>
      </div>

      <div className="agent-overview-legacy agent-overview-kpis">
        <DashboardMetric
          icon={Users}
          label="Total Customers"
          value={Number(summary.customers_total || 0).toLocaleString()}
          trend={newCustomersThisMonth > 0 ? `+${newCustomersThisMonth} this month` : 'No new customers this month'}
          detail={`Email ${verificationSummary.email_verified || 0} · Phone ${verificationSummary.phone_verified || 0}`}
          tone="green"
        />
        <DashboardMetric
          icon={BadgeDollarSign}
          label="Earned Today"
          value={fmtUSD(currentProfit)}
          trend={`${profitChange >= 0 ? '+' : ''}${fmtPct(profitChange)} vs yesterday`}
          detail={`Net profit ${fmtUSD(summary.net_profit_usd)} all-time`}
          tone="green"
        />
        <DashboardMetric
          icon={Wallet}
          label="Customer Deposits"
          value={fmtUSD(summary.deposits_usd)}
          trend="Current customer volume"
          detail={`${reportSummary.customers_with_revenue || 0} customers with movement`}
          tone="blue"
        />
        <DashboardMetric
          icon={CircleDollarSign}
          label="Withdrawals"
          value={fmtUSD(approvedWithdrawalTotal)}
          trend={`${approvedWithdrawals.length} completed`}
          detail={`${fmtUSD(pendingWithdrawalTotal)} pending`}
          tone="amber"
        />
      </div>

      <div className="agent-overview-legacy agent-overview-primary-grid">
        <Panel className="agent-profit-panel">
          <div className="agent-panel-heading">
            <div><h3>Daily Earnings</h3><p>Last 30 days</p></div>
            <button type="button" onClick={() => onSection('analytics')}>View analytics <ChevronRight /></button>
          </div>
          <div className="agent-profit-chart-wrap">
            <ChartContainer
              className="agent-profit-chart"
              config={{ profit: { label: 'Net profit', color: '#16a34a' } }}
            >
              <AreaChart data={profitSeries} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="agentProfitFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a34a" stopOpacity={0.24} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
                <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
                <ChartTooltip content={<ChartTooltipContent formatter={(value) => fmtUSD(value)} />} />
                <Area type="monotone" dataKey="profit" stroke="#16a34a" strokeWidth={3} fill="url(#agentProfitFill)" />
              </AreaChart>
            </ChartContainer>
            <div className="agent-profit-months">
              {profitSeries.slice(-7).map((item) => (
                <div key={item.key}><span>{item.label}</span><strong>{fmtUSD(item.profit)}</strong></div>
              ))}
            </div>
          </div>
        </Panel>

        <Panel className="agent-transactions-panel">
          <div className="agent-panel-heading">
            <div><h3>Recent Transactions</h3><p>Latest earning activity</p></div>
            <button type="button" onClick={() => onSection('ledger')}>View all</button>
          </div>
          <div className="agent-compact-list">
            {recentLedger.length === 0 ? <EmptyState>No earning events yet.</EmptyState> : recentLedger.map((row) => <LedgerRow key={row.id} row={row} compact />)}
          </div>
          <div className="agent-profit-summary">
            <span>Final earnings</span>
            <strong>{fmtUSD(summary.balance_usd)}</strong>
          </div>
        </Panel>

        <Panel className="agent-withdrawal-summary-panel">
          <div className="agent-panel-heading">
            <div><h3>Recent Withdrawals</h3><p>Payout history</p></div>
            <button type="button" onClick={() => onSection('withdrawals')}>View all</button>
          </div>
          <div className="agent-compact-list">
            {recentWithdrawals.length === 0 ? <EmptyState>No withdrawals yet.</EmptyState> : recentWithdrawals.map((row) => <WithdrawalRow key={row.id} row={row} compact />)}
          </div>
          <div className="agent-available-balance">
            <span>Available Balance</span>
            <strong>{fmtUSD(summary.balance_usd)}</strong>
            <button type="button" onClick={onWithdraw}>Withdraw now <ArrowUpRight /></button>
          </div>
        </Panel>
      </div>

      <div className="agent-overview-legacy agent-overview-secondary-grid">
        <Panel className="agent-customer-status-panel">
          <ChartHeader title="Customer Status" description="Current attributed customer health." />
          <div className="agent-status-chart-layout">
            {customerStatus.length ? (
              <ChartContainer className="agent-status-chart" config={{ status: { label: 'Customers', color: '#16a34a' } }}>
                <PieChart>
                  <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                  <Pie data={customerStatus} dataKey="value" nameKey="name" innerRadius={45} outerRadius={65} paddingAngle={2}>
                    {customerStatus.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                </PieChart>
              </ChartContainer>
            ) : <div className="agent-chart-zero">0</div>}
            <div className="agent-status-legend">
              {[
                { label: 'Active', value: activeCustomers, color: '#16a34a' },
                { label: 'Attention', value: attentionCustomers, color: '#f59e0b' },
                { label: 'Inactive', value: inactiveCustomers, color: '#ef4444' },
              ].map((item) => <div key={item.label}><i style={{ backgroundColor: item.color }} /><strong>{item.value}</strong><span>{item.label}</span></div>)}
              <p>Total Customers <strong>{summary.customers_total || 0}</strong></p>
            </div>
          </div>
        </Panel>

        <Panel className="agent-highlights-panel">
          <ChartHeader title="Today's Highlights" description="The numbers that need your attention." />
          <div className="agent-highlight-list">
            <div><NewJoinedIcon /><span>New customers</span><strong>{newCustomersThisMonth}</strong></div>
            <div><Mail /><span>Verified email</span><strong>{verificationSummary.email_verified || 0}</strong></div>
            <div><Smartphone /><span>Verified phone</span><strong>{verificationSummary.phone_verified || 0}</strong></div>
            <div><Link2 /><span>Referral links</span><strong>{assignments.length || 0}</strong></div>
          </div>
        </Panel>

        <Panel className="agent-quick-actions-panel">
          <ChartHeader title="Quick Actions" description={adminPreview ? 'Preview navigation remains available.' : 'Open common tasks in one tap.'} />
          <div className="agent-quick-actions">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button type="button" key={action.id} onClick={() => action.onClick ? action.onClick() : onSection(action.id)}>
                  <Icon />
                  <strong>{action.label}</strong>
                  <span>{action.helper}</span>
                </button>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function DashboardMetric({ icon: Icon, label, value, trend, detail, tone = 'green' }) {
  return (
    <article className={`agent-dashboard-metric agent-v2-metric tone-${tone}`} data-tone={tone}>
      <div className="agent-dashboard-metric-label"><span><Icon /></span><p>{label}</p></div>
      <strong className="agent-v2-money">{value}</strong>
      {trend && <p className="agent-dashboard-metric-trend"><TrendingUp /> {trend}</p>}
      <small>{detail}</small>
    </article>
  );
}

const EARNINGS_WINDOWS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: '7 Days' },
  { id: 'month', label: 'This Month' },
  { id: '90d', label: '90 Days' },
  { id: 'year', label: 'This Year' },
  { id: 'all', label: 'All Time' },
];

function YourEarningsSection({ analytics, summary, adminPreview }) {
  const [range, setRange] = useState('month');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(!adminPreview);
  const [error, setError] = useState('');

  useEffect(() => {
    if (adminPreview) {
      setLoading(false);
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/api/agent-earn/me/earnings?range=${encodeURIComponent(range)}`)
      .then((response) => { if (!cancelled) setReport(response.data || {}); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Could not load your earnings history'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminPreview, range]);

  const fallbackDaily = analytics?.daily || [];
  const daily = report?.daily || fallbackDaily.map((row) => ({ day: row.day, earned_usd: row.earned_usd }));
  const wallet = report?.wallet || analytics?.wallet || {};
  const periodTotal = report?.total?.net_earned_usd ?? daily.reduce((sum, row) => sum + Number(row.earned_usd || 0), 0);
  const today = report?.today || (() => {
    const current = Number(daily.at(-1)?.earned_usd || 0);
    const previous = Number(daily.at(-2)?.earned_usd || 0);
    const delta = current - previous;
    return {
      earned_usd: current,
      yesterday_earned_usd: previous,
      delta_usd: delta,
      percent_change: previous ? (delta / Math.abs(previous)) * 100 : null,
      direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    };
  })();
  const chartData = daily.map((row) => ({ ...row, label: formatDayLabel(row.day) }));
  const entries = report?.entries || [];
  const trendUp = today.direction === 'up';
  const trendDown = today.direction === 'down';
  const trendText = today.percent_change === null || today.percent_change === undefined
    ? (today.delta_usd === 0 ? 'Same as yesterday' : `${trendUp ? 'Up' : 'Down'} ${fmtUSD(Math.abs(today.delta_usd || 0))} vs yesterday`)
    : `${trendUp ? '+' : trendDown ? '−' : ''}${Math.abs(Number(today.percent_change || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}% vs yesterday`;

  return (
    <div className="agent-page agent-your-earnings-page agent-v2-earnings-page space-y-5">
      <SectionTitle
        eyebrow="Your Earnings"
        title="Your Earnings"
        description="Actual commission credits and adjustments. Withdrawal processing is kept separate, so these totals show what you earned."
        action={<span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs font-bold text-[#D4AF37]">{report?.label || (adminPreview ? 'Latest 90 days' : 'Loading…')}</span>}
      />

      {adminPreview && <div className="agent-notice"><ShieldCheck /><div><strong>Read-only admin preview</strong><span>Sign in as the agent to view their complete credited-commission history.</span></div></div>}

      <div className="agent-v2-segments" role="group" aria-label="Earnings period">
        {EARNINGS_WINDOWS.map((window) => (
          <button key={window.id} type="button" aria-pressed={range === window.id} onClick={() => setRange(window.id)} disabled={adminPreview || loading} className={range === window.id ? 'is-active' : ''}>{window.label}</button>
        ))}
      </div>

      {error ? <Panel className="p-5"><EmptyState>{error}</EmptyState></Panel> : <>
        <div className="agent-v2-earnings-overview">
          <Panel className="agent-v2-earnings-hero">
            <span className="agent-v2-kicker"><BadgeDollarSign /> {report?.label || 'Selected period'}</span>
            <p>Net earnings</p>
            <strong>{fmtUSD(periodTotal)}</strong>
            <div className={`agent-v2-trend-pill ${trendDown ? 'is-down' : trendUp ? 'is-up' : 'is-flat'}`}>
              {trendDown ? <ArrowDownRight /> : trendUp ? <ArrowUpRight /> : <TrendingUp />}
              <span>{trendText}</span>
            </div>
            <small>{report?.total?.events ?? daily.filter((row) => Number(row.earned_usd || 0) !== 0).length} earning events in this view</small>
            <img className="agent-v2-earnings-art" src={earningsGrowthWallet} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" />
          </Panel>
          <div className="agent-v2-earnings-side-metrics">
            <DashboardMetric icon={Wallet} label="Available now" value={fmtUSD(wallet.available_usd ?? summary.balance_usd)} detail={`${fmtUSD(wallet.locked_usd)} reserved`} tone="green" />
            <DashboardMetric icon={trendDown ? ArrowDownRight : ArrowUpRight} label="Earned today" value={fmtUSD(today.earned_usd)} detail={trendDown ? 'Below yesterday' : trendUp ? 'Above yesterday' : 'No change'} tone={trendDown ? 'amber' : 'blue'} />
            <DashboardMetric icon={BadgeDollarSign} label="Lifetime earned" value={fmtUSD(wallet.lifetime_earned_usd)} detail="All credited commissions" tone="violet" />
          </div>
        </div>

        <Panel className="agent-v2-chart-panel p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <ChartHeader icon={TrendingUp} title="Earnings Trend" description="A daily view of actual commission ledger entries in the selected period." />
            <div className={`rounded-lg px-3 py-2 text-xs font-bold ${trendUp ? 'bg-green-500/10 text-green-400' : trendDown ? 'bg-red-500/10 text-red-300' : 'bg-white/5 text-gray-400'}`}>{trendUp ? <ArrowUpRight className="mr-1 inline h-4 w-4" /> : trendDown ? <ArrowDownRight className="mr-1 inline h-4 w-4" /> : null}{trendText}</div>
          </div>
          {loading ? <div className="grid min-h-56 place-items-center"><div className="spinner" /></div>
            : chartData.length === 0 ? <EmptyState>No credited commission events in this period.</EmptyState>
              : <ChartContainer className="agent-profit-chart" config={{ earned_usd: { label: 'Earned', color: '#16a34a' } }}>
                <AreaChart data={chartData} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
                  <defs><linearGradient id="agentEarningsHistoryFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16a34a" stopOpacity={0.28} /><stop offset="100%" stopColor="#16a34a" stopOpacity={0.02} /></linearGradient></defs>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
                  <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value) => fmtUSD(value)} />} />
                  <Area type="monotone" dataKey="earned_usd" stroke="#16a34a" strokeWidth={3} fill="url(#agentEarningsHistoryFill)" />
                </AreaChart>
              </ChartContainer>}
        </Panel>

        <Panel className="agent-v2-history-panel overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4"><ChartHeader icon={ScrollText} title="Earnings History" description="Latest commission credits and any adjustments within this filter." /><span className="text-xs text-gray-500">Up to 250 entries</span></div>
          {loading ? <div className="grid min-h-40 place-items-center"><div className="spinner" /></div>
            : entries.length === 0 ? <EmptyState>No earnings entries in this period.</EmptyState>
              : <>
                <div className="space-y-2 p-3 md:hidden">{entries.map((entry) => <article key={entry.id} className="rounded-lg border border-white/10 bg-black/20 p-3"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><strong className="block truncate text-sm text-white">{formatLedgerEventLabel(entry.reason || entry.event_type, 'Commission')}</strong><small className="mt-1 block text-gray-500">{fmtDate(entry.created_at)}</small></div><strong className={Number(entry.net_amount_usd || 0) < 0 ? 'text-red-300' : 'text-green-400'}>{Number(entry.net_amount_usd || 0) >= 0 ? '+' : '−'}{fmtUSD(Math.abs(Number(entry.net_amount_usd || 0)))}</strong></div></article>)}</div>
                <div className="hidden overflow-x-auto md:block" tabIndex={0} aria-label="Earnings history table"><table className="w-full min-w-[650px] text-sm"><thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-gray-500"><th className="p-3">Date</th><th className="p-3">Earning event</th><th className="p-3">Basis</th><th className="p-3 text-right">Amount</th></tr></thead><tbody>{entries.map((entry) => <tr key={entry.id} className="border-b border-white/5"><td className="p-3 text-gray-400">{fmtDate(entry.created_at)}</td><td className="p-3 font-bold text-white">{formatLedgerEventLabel(entry.reason || entry.event_type, 'Commission')}</td><td className="p-3 text-gray-400">{entry.commission_basis || 'Commission'}{entry.commission_percent != null ? ` · ${Number(entry.commission_percent)}%` : ''}</td><td className={`p-3 text-right font-bold tabular-nums ${Number(entry.net_amount_usd || 0) < 0 ? 'text-red-300' : 'text-green-400'}`}>{Number(entry.net_amount_usd || 0) >= 0 ? '+' : '−'}{fmtUSD(Math.abs(Number(entry.net_amount_usd || 0)))}</td></tr>)}</tbody></table></div>
              </>}
        </Panel>
      </>}
    </div>
  );
}

function DetailedReportsSection({ players, coinTracking, pointsMovements, customerTotal }) {
  const totalIn = players.reduce((sum, player) => sum + Number(player.deposits_usd || 0), 0);
  const totalOut = players.reduce((sum, player) => sum + Number(player.withdrawals_usd || 0), 0);
  const combinedFlow = totalIn + totalOut;
  const customerShares = players
    .map((player) => {
      const flow = Number(player.deposits_usd || 0) + Number(player.withdrawals_usd || 0);
      return {
        name: player.full_name || player.username || 'Customer',
        value: combinedFlow > 0 ? Number(((flow / combinedFlow) * 100).toFixed(2)) : 0,
      };
    })
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  const topCustomerShares = customerShares.slice(0, 5).map((row, index) => ({ ...row, fill: REPORT_COLORS[index] }));
  const otherShare = customerShares.slice(5).reduce((sum, row) => sum + row.value, 0);
  const customerFlowData = otherShare > 0
    ? [...topCustomerShares, { name: 'Other Customers', value: Number(otherShare.toFixed(2)), fill: REPORT_COLORS[5] }]
    : topCustomerShares;
  const directCount = players.filter((player) => player.source !== 'referral_tree').length;
  const referralCount = players.filter((player) => player.source === 'referral_tree').length;
  const sourceTotal = directCount + referralCount;
  const sourceData = [
    { name: 'Direct Customers', value: directCount, fill: '#16a34a' },
    { name: 'Referral-Tree Customers', value: referralCount, fill: '#f59e0b' },
  ].filter((row) => row.value > 0);
  const reportCustomerTotal = players.length || Number(customerTotal || 0);
  const customersWithRevenue = players.filter((player) => Number(player.net_revenue_usd || 0) !== 0).length;
  const positiveCustomers = players.filter((player) => Number(player.net_revenue_usd || 0) > 0).length;
  const activePercent = reportCustomerTotal ? (customersWithRevenue / reportCustomerTotal) * 100 : 0;
  const positivePercent = reportCustomerTotal ? (positiveCustomers / reportCustomerTotal) * 100 : 0;
  const inPercent = combinedFlow > 0 ? (totalIn / combinedFlow) * 100 : 0;
  const outPercent = combinedFlow > 0 ? (totalOut / combinedFlow) * 100 : 0;
  const exportCoinTracking = () => {
    if (!coinTracking.length) {
      toast.error('No game coin data to export');
      return;
    }
    const headings = ['game', 'code', 'customers', 'loaded', 'redeemed', 'net_movement', 'current_coins', 'usage_share_percent'];
    const rows = coinTracking.map((row) => [row.game_name, row.game_code, row.players, row.loaded_points, row.redeemed_points, row.net_movement, row.coins, row.usage_share_percent]);
    const csv = [headings, ...rows].map((row) => row.map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'agent-game-coin-report.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="agent-page agent-reports-page agent-v2-reports-page">
      <SectionVisual
        image={reportsInsightLens}
        title="See the signal behind every result."
        description="Compare customer movement, source mix, and game-credit activity without losing the underlying detail."
        action={<span className="agent-date-control"><CalendarDays /><span>Available history</span></span>}
        asHeading
        className="agent-v2-reports-visual"
      />

      <div className="agent-report-kpis">
        <ReportMetric icon={ArrowUpRight} label="In Flow" value={fmtPct(inPercent)} detail="Share of completed movement" tone="green" />
        <ReportMetric icon={ArrowDownRight} label="Out Flow" value={fmtPct(outPercent)} detail="Share of completed movement" tone="red" />
        <ReportMetric icon={Users} label="Revenue Customers" value={customersWithRevenue} detail={`${fmtPct(activePercent)} of this customer list`} tone="blue" />
        <ReportMetric icon={CheckCircle2} label="Positive Customers" value={positiveCustomers} detail={`${fmtPct(positivePercent)} of this customer list`} tone="amber" />
      </div>

      <div className="agent-report-charts-grid">
        <Panel className="agent-flow-panel">
          <ChartHeader icon={BarChart3} title="Customer Flow Percentages" description="Top customers by share of total in/out flow." />
          {customerFlowData.length === 0 ? (
            <EmptyState>No customer flow to graph yet.</EmptyState>
          ) : (
            <div className="agent-donut-layout">
              <ChartContainer className="agent-report-donut" config={{ share: { label: 'Share', color: '#16a34a' } }}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <ChartTooltip content={<ChartTooltipContent hideLabel formatter={(value) => fmtPct(value)} />} />
                  <Pie data={customerFlowData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="48%" outerRadius="72%" paddingAngle={1} isAnimationActive={false}>
                    {customerFlowData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div className="agent-flow-legend">
                {customerFlowData.map((row) => (
                  <div key={row.name}><i style={{ backgroundColor: row.fill }} /><span>{row.name}</span><strong>{fmtPct(row.value)}</strong></div>
                ))}
              </div>
            </div>
          )}
        </Panel>

        <Panel className="agent-source-panel">
          <ChartHeader icon={GitBranch} title="Source Split" description="Direct customers vs referral-tree customers." />
          {sourceData.length === 0 ? (
            <EmptyState>No customers to split yet.</EmptyState>
          ) : (
            <div className="agent-donut-layout agent-source-layout">
              <ChartContainer className="agent-report-donut" config={{ source: { label: 'Customers', color: '#16a34a' } }}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                  <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="48%" outerRadius="72%" paddingAngle={1} isAnimationActive={false}>
                    {sourceData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div className="agent-source-copy">
                {sourceData.map((row) => (
                  <div key={row.name}><i style={{ backgroundColor: row.fill }} /><span>{row.name}</span><strong>{fmtPct(sourceTotal ? (row.value / sourceTotal) * 100 : 0)} <small>({row.value})</small></strong></div>
                ))}
                <p><GitBranch /> Referral-tree customers contribute {fmtPct(sourceTotal ? (referralCount / sourceTotal) * 100 : 0)} of attributed customers.</p>
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Panel className="agent-coin-panel">
        <div className="agent-table-heading">
          <ChartHeader icon={CoinStackIcon} title="Game-Wise Coin Tracking" description="Provider and game balances grouped by customer accounts." />
          <button type="button" onClick={exportCoinTracking}><Download /> Export CSV</button>
        </div>
        {coinTracking.length > 0 && <div className="agent-v2-mobile-records">
          {coinTracking.map((row) => (
            <article key={`mobile-${row.game_code}`} className="agent-v2-mobile-record">
              <div className="agent-v2-mobile-record-head">
                <div className="agent-game-identity">
                  <AgentGameIcon game={row} />
                  <div><strong>{row.game_name}</strong><small>{row.players} customers · {row.game_code}</small></div>
                </div>
                <strong>{fmtPct(row.usage_share_percent)}</strong>
              </div>
              <dl><div><dt>Loaded</dt><dd>{Number(row.loaded_points || 0).toLocaleString()}</dd></div><div><dt>Redeemed</dt><dd>{Number(row.redeemed_points || 0).toLocaleString()}</dd></div><div><dt>Net movement</dt><dd>{Number(row.net_movement || 0).toLocaleString()}</dd></div><div><dt>Current coins</dt><dd>{Number(row.coins || 0).toLocaleString()}</dd></div></dl>
            </article>
          ))}
        </div>}
        <div className="agent-table-scroll agent-v2-desktop-records" tabIndex={0} aria-label="Game coin tracking table">
          <table className="agent-data-table">
            <thead>
              <tr>
                <th>Game</th>
                <th>Customers</th>
                <th>Loaded</th>
                <th>Redeemed</th>
                <th>Net Movement</th>
                <th>Current Coins</th>
                <th>Usage Share</th>
              </tr>
            </thead>
            <tbody>
              {coinTracking.length === 0 ? (
                <tr><td colSpan={7} className="agent-table-empty">No game coin data yet.</td></tr>
              ) : coinTracking.map((row) => (
                <tr key={row.game_code}>
                  <td><div className="agent-game-identity is-compact"><AgentGameIcon game={row} compact /><div><strong>{row.game_name}</strong><span>{row.game_code}</span></div></div></td>
                  <td>{row.players}</td>
                  <td className="is-positive">{Number(row.loaded_points || 0).toLocaleString()}</td>
                  <td className="is-negative">{Number(row.redeemed_points || 0).toLocaleString()}</td>
                  <td className={Number(row.net_movement || 0) < 0 ? 'is-negative' : 'is-positive'}>{Number(row.net_movement || 0).toLocaleString()}</td>
                  <td className="is-positive">{Number(row.coins || 0).toLocaleString()}</td>
                  <td>{fmtPct(row.usage_share_percent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="agent-report-note"><ShieldCheck /> Completed movements power totals. Pending and failed movements remain visible below but do not affect totals.</p>
      </Panel>

      <Panel className="agent-coin-panel">
        <div className="agent-table-heading">
          <ChartHeader icon={CoinStackIcon} title="Points Movements" description="Customer game loads, redeems, refunds, and pending provider activity." />
        </div>
        <PointsMovementTable rows={pointsMovements} />
      </Panel>
    </div>
  );
}

function ReportMetric({ icon: Icon, label, value, detail, tone }) {
  return (
    <article className={`agent-report-metric agent-v2-metric tone-${tone}`} data-tone={tone}>
      <div><Icon /><span>{label}</span></div>
      <strong className="agent-v2-money">{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function ChartHeader({ icon: Icon, title, description }) {
  return (
    <div className="agent-v2-card-heading flex items-start gap-2">
      {Icon && <span className="agent-v2-card-heading-icon"><Icon /></span>}
      <div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
    </div>
  );
}

function TenantLinksSection({ assignments, adminPreview, onCopy }) {
  return (
    <div className="agent-page agent-v2-links-page space-y-5">
      <SectionVisual
        image={customerLinksBeacon}
        title="One link, a lasting customer relationship."
        description={adminPreview ? 'Admin preview of the customer links assigned to this agent.' : 'Every valid signup route keeps customer attribution clear while connection and promo codes stay separate.'}
        asHeading
        className="agent-v2-links-visual"
      />
      <Panel className="p-4">
        {assignments.length === 0 ? (
          <EmptyState>No referral links assigned yet.</EmptyState>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {assignments.map((assignment) => (
              <div key={assignment.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-black">{assignment.tenant?.name || assignment.tenant_id}</h3>
                    <p className="text-xs text-gray-400">{assignment.commission_basis} · {assignment.commission_percent}% · {assignment.include_referral_tree ? 'tree on' : 'tree off'}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] px-2 py-1 rounded-full ${assignment.active === false ? 'bg-red-500/15 text-red-300' : 'bg-green-500/15 text-green-300'}`}>{assignment.active === false ? 'Paused' : 'Active'}</span>
                </div>
                <div className="mt-4 space-y-3">
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Signup Link</p>
                    <div className="flex items-center gap-2">
                      <code className="agent-referral-code min-w-0 flex-1 truncate rounded-lg px-3 py-2 text-xs">{assignment.tracking_url || assignment.tracking_code}</code>
                      <Button size="sm" variant="outline" className="h-10 border-white/10" onClick={() => onCopy(assignment.tracking_url || assignment.tracking_code, 'Link copied')} aria-label="Copy referral link">
                        <Copy className="w-4 h-4" />
                      </Button>
                      {assignment.tracking_url && (
                        <a href={assignment.tracking_url} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-gray-300 hover:text-white" aria-label="Open referral link">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Agent Connection Code</p>
                    <div className="flex items-center gap-2">
                      <code className="agent-referral-code min-w-0 flex-1 truncate rounded-lg px-3 py-2 text-xs font-black">{assignment.tracking_code || 'No code'}</code>
                      <Button size="sm" variant="outline" className="h-10 border-white/10" disabled={!assignment.tracking_code} onClick={() => onCopy(assignment.tracking_code, 'Agent connection code copied')} aria-label="Copy agent connection code">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">Players who did not use your link can apply this in Profile within 24 hours. It connects them to you; it does not award FP.</p>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Player Promo Code</p>
                    {assignment.player_promo?.code ? (
                      <>
                      <div className="flex items-center gap-2">
                        <code className="agent-referral-code is-promo min-w-0 flex-1 truncate rounded-lg px-3 py-2 text-xs font-black">{assignment.player_promo.code}</code>
                        <Button size="sm" variant="outline" className="h-10 border-white/10" onClick={() => onCopy(assignment.player_promo.code, 'Player promo code copied')} aria-label="Copy player promo code">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">Awards {assignment.player_promo.fp_amount || 0} FP when redeemed. It never changes a player’s referred agent.</p>
                      </>
                    ) : (
                      <p className="rounded-lg border border-dashed border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-gray-500">No player promo code is set for this link yet. Your admin can create one in Agents Hub.</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function PlayersSection({ players, onRemove, onChat, adminPreview, theme }) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const now = new Date();
  const filteredPlayers = players.filter((player) => {
    const joined = new Date(player.created_at || player.joined_at);
    const isNew = !Number.isNaN(joined.getTime()) && (now.getTime() - joined.getTime()) <= 30 * 24 * 60 * 60 * 1000;
    const isActive = player.active !== false && !player.disabled;
    const matchesFilter = filter === 'all' || (filter === 'active' && isActive) || (filter === 'new' && isNew) || (filter === 'inactive' && !isActive);
    const haystack = `${player.full_name || ''} ${player.username || ''} ${player.player_id || ''}`.toLowerCase();
    return matchesFilter && haystack.includes(query.trim().toLowerCase());
  });
  const counts = {
    all: players.length,
    active: players.filter((player) => player.active !== false && !player.disabled).length,
    new: players.filter((player) => {
      const joined = new Date(player.created_at || player.joined_at);
      return !Number.isNaN(joined.getTime()) && (now.getTime() - joined.getTime()) <= 30 * 24 * 60 * 60 * 1000;
    }).length,
    inactive: players.filter((player) => player.active === false || player.disabled).length,
  };

  const openPreview = async (player) => {
    setSelectedPlayer(player);
    setPreview(null);
    if (adminPreview) {
      setPreview({ customer: player, game_accounts: [], recent_activity: [], points_movements: [] });
      return;
    }
    try {
      setPreviewLoading(true);
      const response = await api.get(`/api/agent-earn/me/players/${player.player_id}/preview`);
      setPreview(response.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Customer preview could not load');
      setSelectedPlayer(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="agent-page agent-customers-page agent-v2-customers-page space-y-5">
      <SectionTitle
        eyebrow="Customer ownership"
        title="My Customers"
        description="Customers attributed to your links. Detailed finance movement stays inside Reports."
      />
      <SectionVisual
        image={customersOwnedNetwork}
        eyebrow="Your network"
        title="Every attributed customer in one view."
        description="Find the right customer, review their activity, and open a direct support conversation."
        className="agent-v2-customers-visual"
      />
      <div className="agent-v2-customer-toolbar">
        <label className="agent-search-control"><Search /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by customer name, username, or ID" /></label>
        <div className="agent-v2-segments" role="group" aria-label="Customer status">
          {['all', 'active', 'new', 'inactive'].map((value) => (
            <button key={value} type="button" aria-pressed={filter === value} className={filter === value ? 'is-active' : ''} onClick={() => setFilter(value)}>
              {value.charAt(0).toUpperCase() + value.slice(1)} <span>{counts[value]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="agent-customers-mobile">
        <div className="agent-mobile-page-title"><div><h1>Customers</h1><p>Your attributed customer network</p></div><CustomersGroupIcon /></div>
        <label className="agent-mobile-customer-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customers..." /></label>
        <div className="agent-mobile-customer-filters">
          {['all', 'active', 'new', 'inactive'].map((value) => (
            <button key={value} type="button" aria-pressed={filter === value} className={filter === value ? 'is-active' : ''} onClick={() => setFilter(value)}>
              <span>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
              <em>({counts[value]})</em>
            </button>
          ))}
        </div>
        <div className="agent-mobile-customer-list">
          {filteredPlayers.length ? filteredPlayers.map((player) => {
            const name = player.full_name || player.username || 'Customer';
            const active = player.active !== false && !player.disabled;
            const transactionCount = Number(player.deposit_count || 0) + Number(player.withdrawal_count || 0);
            return (
              <button key={player.player_id} type="button" onClick={() => openPreview(player)}>
                <span className="agent-mobile-customer-avatar">{String(name).trim().charAt(0).toUpperCase()}<i className={active ? 'is-active' : ''} /></span>
                <span><strong>{name}</strong><small>{active ? 'Active customer' : 'Inactive customer'}</small><em>{formatPlayerSource(player)} · {transactionCount} transactions</em></span>
                <ChevronRight />
              </button>
            );
          }) : <EmptyState>No customers match this filter.</EmptyState>}
        </div>
      </div>
      <Panel className="overflow-hidden agent-customers-desktop">
        <div className="overflow-x-auto" tabIndex={0} aria-label="Customers table">
          <table className="w-full text-sm" data-testid="agent-player-table">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-xs text-gray-500">
                <th className="text-left py-3 px-4">Customer</th>
                <th className="text-left py-3 px-4">Source</th>
                <th className="text-left py-3 px-4">Joined</th>
                <th className="text-right py-3 px-4">Activity</th>
                <th className="text-right py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-gray-500">No customers match this view.</td></tr>
              ) : filteredPlayers.map((player) => (
                <tr key={player.player_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="py-3 px-4">
                    <p className="font-semibold">{player.full_name || player.username || 'Customer'}</p>
                    <p className="text-[10px] text-gray-500">{player.username || player.player_id}</p>
                  </td>
                  <td className="py-3 px-4 text-gray-300">
                    <p>{formatPlayerSource(player)}</p>
                    {player.agent_name && <p className="text-[10px] text-gray-500">Agent: {player.agent_name}</p>}
                  </td>
                  <td className="py-3 px-4 text-gray-400">{fmtShortDate(player.created_at || player.joined_at)}</td>
                  <td className="py-3 px-4 text-right"><strong>{Number(player.deposit_count || 0) + Number(player.withdrawal_count || 0)}</strong><p className="text-[10px] text-gray-500">transactions</p></td>
                  <td className="py-3 px-4 text-right">
                    <StatusBadge status={player.active === false || player.disabled ? 'inactive' : 'active'} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="agent-player-actions">
                      <Button size="sm" variant="outline" className="agent-preview-button" onClick={() => openPreview(player)}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                      {!adminPreview && (
                        <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 text-xs" onClick={() => onRemove(player)}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <CustomerPreviewDialog
        open={Boolean(selectedPlayer)}
        loading={previewLoading}
        data={preview}
        fallbackPlayer={selectedPlayer}
        theme={theme}
        onChat={onChat}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPlayer(null);
            setPreview(null);
          }
        }}
      />
    </div>
  );
}

function CustomerPreviewDialog({ open, loading, data, fallbackPlayer, theme, onChat, onOpenChange }) {
  const customer = data?.customer || fallbackPlayer || {};
  const gameAccounts = data?.game_accounts || [];
  const activity = data?.recent_activity || [];
  const canChat = Boolean(customer.player_id || customer.id);

  const handleChat = () => {
    if (!canChat) return;
    onChat?.(customer);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-ui="studio" data-theme={theme} overlayClassName="agent-preview-overlay" className="agent-customer-preview-dialog">
        <DialogHeader className="agent-customer-preview-header">
          <div className="agent-customer-avatar">{String(customer.full_name || customer.username || 'C').trim().charAt(0).toUpperCase()}</div>
          <div>
            <DialogTitle>{customer.full_name || customer.username || 'Customer'}</DialogTitle>
            <DialogDescription>{customer.username || customer.player_id || 'Attributed customer'}</DialogDescription>
          </div>
          <button type="button" className="agent-preview-chat-button" aria-label="Chat with this customer" onClick={handleChat} disabled={!canChat}>
            <MessageCircle />
            <span>Chat</span>
          </button>
        </DialogHeader>

        {loading ? (
          <div className="agent-preview-loading"><div className="spinner" /><p>Loading customer details</p></div>
        ) : (
          <Tabs defaultValue="overview" className="agent-preview-tabs">
            <TabsList className="agent-preview-tab-list">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="games">Game Accounts <span>{gameAccounts.length}</span></TabsTrigger>
              <TabsTrigger value="activity">Activity <span>{activity.length}</span></TabsTrigger>
            </TabsList>
            <ScrollArea className="agent-customer-preview-scroll">
              <TabsContent value="overview" className="agent-preview-tab-content">
                <div className="agent-preview-metrics">
                  <PreviewMetric label="Completed deposits" value={fmtUSD(customer.deposits_usd)} helper={`${customer.deposit_count || 0} payments`} tone="green" />
                  <PreviewMetric label="Completed cashouts" value={fmtUSD(customer.withdrawals_usd)} helper={`${customer.withdrawal_count || 0} payouts`} tone="red" />
                  <PreviewMetric label="Net revenue" value={fmtUSD(customer.net_revenue_usd)} helper="Completed payments only" tone={Number(customer.net_revenue_usd || 0) < 0 ? 'red' : 'gold'} />
                  <PreviewMetric label="Wallet balance" value={fmtUSD(customer.cash_balance)} helper={`${Number(customer.fp_balance || 0).toLocaleString()} FP`} tone="blue" />
                </div>
                <div className="agent-preview-detail-grid">
                  <PreviewDetail icon={Link2} label="Source" value={formatPlayerSource(customer)} />
                  <PreviewDetail icon={CalendarDays} label="Joined" value={fmtShortDate(customer.joined_at || customer.created_at)} />
                </div>
              </TabsContent>

              <TabsContent value="games" className="agent-preview-tab-content">
                {gameAccounts.length === 0 ? (
                  <EmptyState>No game accounts yet.</EmptyState>
                ) : (
                  <div className="agent-game-account-grid">
                    {gameAccounts.map((account) => (
                      <article key={account.id || `${account.game_id}-${account.provider_username}`} className="agent-game-account-card">
                        <div className="agent-game-account-icon"><Gamepad2 /></div>
                        <div className="agent-game-account-copy">
                          <div><strong>{account.game_name}</strong><StatusBadge status={account.status} /></div>
                          <p>{account.provider_username || account.provider_user_id || 'Provider ID unavailable'}</p>
                          <span>Added {fmtShortDate(account.created_at)}</span>
                        </div>
                        <div className="agent-game-account-balance"><span>Current balance</span><strong>{Number(account.current_balance || 0).toLocaleString()}</strong></div>
                      </article>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="activity" className="agent-preview-tab-content">
                <CustomerActivityList rows={activity} />
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewMetric({ label, value, helper, tone }) {
  return (
    <article className={`agent-preview-metric tone-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{helper}</small>
    </article>
  );
}

function PreviewDetail({ icon: Icon, label, value, ok }) {
  return (
    <div className="agent-preview-detail">
      <span><Icon /></span>
      <div><small>{label}</small><strong className={ok === false ? 'is-negative' : ok === true ? 'is-positive' : ''}>{value}</strong></div>
    </div>
  );
}

function formatPlayerSource(player) {
  if (player.source === 'promo_code' || player.source === 'agent_code') return 'Agent Code';
  if (player.source !== 'referral_tree') return 'Direct';
  const depth = player.referral_depth ?? player.depth;
  return Number(depth) > 0 ? `Referral Tree · L${depth}` : 'Referral Tree';
}

function InviteAgentSection({ agentReferral, onCopy }) {
  const referralPercent = Number(agentReferral?.commission_percent ?? 5);
  const referralPercentLabel = Number.isInteger(referralPercent) ? referralPercent.toFixed(0) : referralPercent.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  const referredAgents = agentReferral?.referred_agents || [];

  return (
    <div className="agent-page agent-invite-agent-page agent-v2-invite-page">
      <Panel className="agent-v2-illustrated-hero agent-v2-invite-hero">
        <div className="agent-v2-illustrated-copy">
          <span className="agent-v2-kicker"><InviteAgentIcon /> Partner-to-partner growth</span>
          <h3>Earn {referralPercentLabel}% of every referred agent’s eligible earnings.</h3>
          <p>The company pays your referral share separately. The invited agent keeps their normal rates, earnings, and payout rules.</p>
          <div className="agent-v2-hero-proof">
            <span><ShieldCheck /> Their earnings never get reduced</span>
            <span><Network /> Referred agents stay visible here</span>
          </div>
          {agentReferral?.unlocked && agentReferral?.referral_link ? (
            <button type="button" className="agent-v2-hero-cta" onClick={() => onCopy(agentReferral.referral_link, 'Agent invitation link copied')}><Copy /> Copy invitation link</button>
          ) : (
            <div className="agent-v2-locked-message"><LockKeyhole /><p><strong>Invitation link locked</strong><span>{agentReferral?.required_unlock || 'Security deposit or investment unlock required.'}</span></p></div>
          )}
        </div>
        <div className="agent-v2-illustrated-art">
          <img src={inviteNetworkHero} width="1586" height="992" loading="lazy" decoding="async" alt="" aria-hidden="true" />
        </div>
      </Panel>

      <div className="agent-referral-kpis agent-invite-agent-kpis">
        <ReportMetric icon={Network} label="Referred agents" value={agentReferral?.referred_agents_count || 0} detail={agentReferral?.unlocked ? 'Invitation link active' : 'Unlock required'} tone="violet" />
        <ReportMetric icon={CircleDollarSign} label="Your referral share" value={`${referralPercentLabel}%`} detail="Paid by the company" tone="amber" />
        <ReportMetric icon={ShieldCheck} label="Their earning rules" value="Unchanged" detail="Normal agent rules continue" tone="green" />
      </div>

      <Panel className="agent-referred-agents-panel">
        <ChartHeader icon={Users} title="Referred Agents" description="Agents who signed up through your invitation link." />
        {referredAgents.length ? (
          <div className="agent-referred-agents-list">
            {referredAgents.map((referredAgent) => (
              <article key={referredAgent.id}>
                <span>{String(referredAgent.label || 'A').charAt(0).toUpperCase()}<i className={referredAgent.active ? 'is-active' : ''} /></span>
                <div><strong>{referredAgent.label || 'Agent'}</strong><small>Joined {fmtShortDate(referredAgent.joined_at)}</small></div>
                <b>{referredAgent.active ? 'Active' : 'Inactive'}</b>
              </article>
            ))}
          </div>
        ) : <EmptyState>No referred agents yet. Share your invitation link to get started.</EmptyState>}
      </Panel>
    </div>
  );
}

function ReferralSection({ referralTree }) {
  const nodes = referralTree?.nodes || [];
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const activeNodes = nodes.filter((node) => node.active !== false);
  const filteredNodes = nodes.filter((node) => {
    const matchesSearch = !query.trim() || String(node.label || '').toLowerCase().includes(query.trim().toLowerCase());
    const matchesLevel = levelFilter === 'all' || Number(node.depth || 0) === Number(levelFilter);
    return matchesSearch && matchesLevel;
  });
  const maxDepth = Math.max(Number(referralTree?.max_depth || 0), ...nodes.map((node) => Number(node.depth || 0)), 0);
  const levels = Array.from({ length: maxDepth + 1 }, (_, depth) => ({
    depth,
    nodes: filteredNodes.filter((node) => Number(node.depth || 0) === depth),
  })).filter((level) => level.nodes.length > 0);
  const childCounts = nodes.reduce((map, node) => {
    if (node.parent_id) map.set(node.parent_id, (map.get(node.parent_id) || 0) + 1);
    return map;
  }, new Map());
  const exportTree = () => {
    if (!nodes.length) {
      toast.error('No referral tree to export');
      return;
    }
    const rows = ['customer,level,status,source', ...nodes.map((node) => [node.label, node.depth || 0, node.active === false ? 'inactive' : 'active', node.source].map((value) => `"${String(value || '').replaceAll('"', '""')}"`).join(','))];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'agent-referral-tree.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="agent-page agent-referral-page agent-v2-referral-page">
      <SectionVisual
        image={referralTreeGrowthSpot}
        title="Your customer network, mapped clearly."
        description="Every customer stays connected to the level and source recorded by your customer link."
        action={<button type="button" className="agent-secondary-button" onClick={exportTree}><Download /> Export tree</button>}
        asHeading
        className="agent-v2-referral-visual"
      />

      <div className="agent-referral-kpis">
        <ReportMetric icon={Users} label="Total Referrals" value={nodes.length} detail="All attributed customers" tone="blue" />
        <ReportMetric icon={ActiveCustomerIcon} label="Active Referrals" value={activeNodes.length} detail={`${fmtPct(nodes.length ? (activeNodes.length / nodes.length) * 100 : 0)} of total`} tone="green" />
        <ReportMetric icon={TrendingUp} label="Tree Depth" value={maxDepth} detail={`${referralTree?.root_count || 0} direct roots`} tone="green" />
      </div>

      <Panel className="agent-referral-tree-panel">
        <div className="agent-tree-toolbar">
          <div className="agent-tree-status-key"><span><i className="is-active" /> Active</span><span><i /> Inactive</span></div>
          <label className="agent-search-control"><Search /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customer" /></label>
          <select value={levelFilter} onChange={(event) => setLevelFilter(event.target.value)} aria-label="Filter referral tree by level">
            <option value="all">All levels</option>
            {Array.from({ length: maxDepth + 1 }, (_, depth) => <option key={depth} value={depth}>Level {depth}</option>)}
          </select>
        </div>

        {nodes.length === 0 ? (
          <EmptyState>No referral tree yet. Share a referral link to start growth.</EmptyState>
        ) : filteredNodes.length === 0 ? (
          <EmptyState>No referrals match this filter.</EmptyState>
        ) : (
          <div className="agent-tree-scroll" tabIndex="0" role="region" aria-label="Referral tree grouped by level">
            <div className="agent-tree-canvas">
              <div className="agent-tree-root">
                <span>A</span>
                <div><strong>Your agent account</strong><small>You · Level 0</small></div>
                <p><span>Direct roots</span><strong>{referralTree?.root_count || 0}</strong></p>
              </div>
              {levels.map((level) => (
                <div className="agent-tree-level" key={level.depth} data-depth={level.depth}>
                  <div className="agent-tree-level-label">Level {level.depth}</div>
                  <div className="agent-tree-node-row">
                    {level.nodes.map((node) => (
                      <article className="agent-tree-node" key={`${node.id}-${node.depth}`}>
                        <div className="agent-tree-node-main">
                          <span>{String(node.label || 'C').charAt(0).toUpperCase()}<i className={node.active === false ? '' : 'is-active'} /></span>
                          <div><strong>{node.label || 'Customer'}</strong><small>{node.source === 'referral_tree' ? 'Referral customer' : 'Direct customer'}</small></div>
                        </div>
                        <div className="agent-tree-node-stats"><span>Level <strong>{node.depth || 0}</strong></span><span>Referrals <strong>{childCounts.get(node.id) || 0}</strong></span></div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

    </div>
  );
}

function TeamChatSection({ adminPreview, channels, onSection, unreadCounts, onTeamUnreadChange }) {
  const fallbackChannels = [
    { id: 'communication', label: 'Communication Team', description: 'Handles customer communication' },
    { id: 'customer_support', label: 'Customer Support Team', description: 'Solves customer support issues' },
    { id: 'confirmation', label: 'Confirmation Team', description: 'Verifies payments and transactions' },
    { id: 'managers', label: 'Managers', description: 'Handles approvals and escalated issues' },
    { id: 'development', label: 'Development Team', description: 'Fixes website and technical issues' },
  ];
  const availableChannels = Array.isArray(channels) && channels.length ? channels : fallbackChannels;
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [mobilePane, setMobilePane] = useState('list');
  const [conversationQuery, setConversationQuery] = useState('');
  const messageRequestRef = useRef(0);

  const loadConversations = useCallback(async ({ quiet = false } = {}) => {
    if (adminPreview) return;
    if (!quiet) setLoading(true);
    try {
      const res = await api.get('/api/agent-earn/me/team-chat');
      const nextConversations = res.data?.conversations || [];
      setConversations(nextConversations);
      onTeamUnreadChange?.(
        res.data?.unread_count ?? nextConversations.reduce(
          (sum, conversation) => sum + Math.max(0, Number(conversation.agent_unread) || 0),
          0,
        ),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Team chat could not load');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [adminPreview, onTeamUnreadChange]);

  const openConversation = useCallback(async (conversation) => {
    if (!conversation?.team || adminPreview) return;
    const requestId = messageRequestRef.current + 1;
    messageRequestRef.current = requestId;
    setSelected(conversation);
    setMessages([]);
    setMobilePane('chat');
    if (!conversation.id) {
      return;
    }
    try {
      const res = await api.get(`/api/agent-earn/me/team-chat/${conversation.id}/messages`);
      if (messageRequestRef.current !== requestId) return;
      setSelected({ ...conversation, ...(res.data?.conversation || {}) });
      setMessages(res.data?.messages || []);
      await loadConversations({ quiet: true });
    } catch (err) {
      if (messageRequestRef.current !== requestId) return;
      toast.error(err.response?.data?.detail || 'Messages could not load');
    }
  }, [adminPreview, loadConversations]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const sendReply = async () => {
    const message = draft.trim();
    if (!selected?.team || !message || adminPreview || sending) return;
    setSending(true);
    try {
      let conversation = selected;
      if (selected.id) {
        await api.post(`/api/agent-earn/me/team-chat/${selected.id}/messages`, { message });
      } else {
        const res = await api.post('/api/agent-earn/me/team-chat', {
          team: selected.team,
          issue_type: 'direct_chat',
          subject: `${selected.team_label || 'Team'} chat`,
          message,
        });
        conversation = { ...selected, ...(res.data?.conversation || {}) };
      }
      setDraft('');
      await loadConversations();
      await openConversation(conversation);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const latestByTeam = new Map();
  conversations.forEach((conversation) => {
    if (!latestByTeam.has(conversation.team)) latestByTeam.set(conversation.team, conversation);
  });
  const directConversations = availableChannels.map((channel) => {
    const latest = latestByTeam.get(channel.id);
    return latest ? { ...latest, description: channel.description || latest.description } : {
      id: null,
      team: channel.id,
      team_label: channel.label,
      description: channel.description,
      last_message: 'Start a direct conversation',
      message_count: 0,
      agent_unread: 0,
    };
  });
  const visibleConversations = directConversations.filter((conversation) => {
    const query = conversationQuery.trim().toLowerCase();
    if (!query) return true;
    return [conversation.team_label, conversation.subject, conversation.last_message]
      .some((value) => String(value || '').toLowerCase().includes(query));
  });

  return (
    <div className="agent-page agent-team-chat-page agent-v2-team-chat-page">
      <AgentChatMobileModeSwitch active="team" onSection={onSection} unreadCounts={unreadCounts} />
      {adminPreview && (
        <div className="agent-notice" role="status"><ShieldCheck /><div><strong>Read-only admin preview</strong><span>Sign in as an agent to send team messages.</span></div></div>
      )}

      <div className="agent-team-layout">
        <Panel className={`agent-team-list-pane ${mobilePane === 'list' ? 'is-mobile-active' : ''}`}>
          <div className="agent-team-list-heading">
            <div><h3>Teams</h3><UnreadBadge count={unreadCounts?.team} label="unread team messages" /></div>
            <button type="button" onClick={() => loadConversations()} disabled={loading || adminPreview} aria-label="Refresh team chats"><RefreshCw /></button>
          </div>
          <label className="agent-search-control"><Search /><Input value={conversationQuery} onChange={(event) => setConversationQuery(event.target.value)} placeholder="Search teams" /></label>
          <div className="agent-conversation-list">
            {visibleConversations.length === 0 ? (
              <EmptyState>No teams match your search.</EmptyState>
            ) : visibleConversations.map((conversation) => {
              const TeamIcon = TEAM_ICONS[conversation.team] || MessageSquareText;
              const unread = Math.max(0, Number(conversation.agent_unread) || 0);
              return (
                <button type="button" key={conversation.team} onClick={() => openConversation(conversation)} className={`${selected?.team === conversation.team ? 'is-active' : ''} ${unread ? 'is-unread' : ''}`.trim()} disabled={adminPreview} aria-label={`${conversation.team_label || 'Support Team'}${unread ? `, ${unread} unread message${unread === 1 ? '' : 's'}` : ''}`}>
                  <span className="agent-team-avatar"><TeamIcon /></span>
                  <span className="agent-conversation-copy">
                    <span><strong>{conversation.team_label || 'Support Team'}</strong>{conversation.id && <small>{fmtDate(conversation.updated_at || conversation.created_at)}</small>}<UnreadBadge count={unread} label="unread messages" /></span>
                    {conversation.id ? (
                      <p className="agent-team-message-preview">{conversation.last_message || 'Open conversation'}</p>
                    ) : (
                      <p className="agent-team-purpose">{conversation.description || 'Direct team support'}</p>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel className={`agent-team-message-pane ${mobilePane === 'chat' ? 'is-mobile-active' : ''}`}>
          {!selected ? (
            <div className="agent-message-empty agent-v2-team-empty"><img src={teamSupportChannels} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" /><strong>Select a team</strong><p>Your direct conversation will appear here.</p></div>
          ) : (
            <>
              <div className="agent-message-header">
                <button type="button" className="agent-message-back" onClick={() => setMobilePane('list')} aria-label="Back to team chats"><ArrowLeft /></button>
                <span className="agent-team-avatar">{(() => { const TeamIcon = TEAM_ICONS[selected.team] || MessageSquareText; return <TeamIcon />; })()}</span>
                <div><strong>{selected.team_label || 'Support Team'}</strong><p>{selected.description || selected.subject || 'Direct team support'}</p></div>
              </div>
              <div className="agent-message-thread">
                {messages.length === 0 ? <div className="agent-message-empty agent-v2-team-empty is-compact"><img src={teamSupportChannels} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" /><strong>Start the conversation</strong><p>Send a message directly to {selected.team_label || 'the team'}.</p></div> : messages.map((message) => (
                  <div key={message.id} className={`agent-chat-row ${message.sender_role === 'agent' ? 'is-agent' : 'is-team'}`}>
                    <div className="agent-chat-bubble">
                      <strong>{message.sender_name || (message.sender_role === 'agent' ? 'Agent' : 'Team')}</strong>
                      <p>{message.body}</p>
                      <span>{fmtDate(message.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="agent-message-composer">
                <Textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={2}
                  placeholder="Type your message..."
                  disabled={adminPreview}
                />
                <Button onClick={sendReply} disabled={sending || adminPreview || !draft.trim()} className="agent-primary-button" aria-label="Send message"><Send /></Button>
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

function FlowRow({ label, helper, amount, kind = 'neutral' }) {
  const tone = kind === 'cut' ? 'text-red-400' : kind === 'gain' ? 'text-green-400' : kind === 'final' ? 'text-[#D4AF37]' : 'text-white';
  const numericAmount = Number(amount || 0);
  const sign = kind === 'cut'
    ? (numericAmount < 0 ? '+' : '−')
    : kind === 'gain'
      ? (numericAmount < 0 ? '−' : '+')
      : numericAmount < 0 ? '−' : '';
  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 ${kind === 'final' ? 'border border-[#D4AF37]/30 bg-[#D4AF37]/10' : kind === 'total' ? 'border border-white/15 bg-white/5' : ''}`}>
      <div className="min-w-0">
        <p className={`truncate text-sm font-bold ${kind === 'final' ? 'text-[#D4AF37]' : 'text-white'}`}>{label}</p>
        {helper && <p className="truncate text-[11px] text-gray-500">{helper}</p>}
      </div>
      <strong className={`shrink-0 tabular-nums ${tone}`}>{sign}{fmtUSD(Math.abs(numericAmount))}</strong>
    </div>
  );
}

function AgentAnalyticsSection({ analytics }) {
  const [range, setRange] = useState(30);
  const daily = analytics?.daily || [];
  const wallet = analytics?.wallet || null;
  const profitSharing = analytics?.breakdown?.profit_sharing || analytics?.profit_sharing || null;
  const visible = daily.slice(-range);
  const rangeTotals = visible.reduce((acc, row) => ({
    in_usd: acc.in_usd + Number(row.in_usd || 0),
    out_usd: acc.out_usd + Number(row.out_usd || 0),
    points_used: acc.points_used + Number(row.points_used || 0),
    earned_usd: acc.earned_usd + Number(row.earned_usd || 0),
  }), { in_usd: 0, out_usd: 0, points_used: 0, earned_usd: 0 });
  const chartData = visible.map((row) => ({
    ...row,
    label: formatDayLabel(row.day),
  }));
  const recentDays = [...visible].reverse().filter((row) => row.in_usd || row.out_usd || row.points_used || row.earned_usd).slice(0, 14);

  if (!analytics) {
    return (
      <div className="agent-page agent-v2-analytics-page space-y-5">
        <Panel className="p-6"><EmptyState>No analytics yet.</EmptyState></Panel>
      </div>
    );
  }

  return (
    <div className="agent-page agent-v2-analytics-page space-y-5">
      <SectionVisual
        image={analyticsFlowMapSpot}
        title="Follow the path from activity to earnings."
        description="Customer flow, configured costs, and your credited share stay connected in one transparent view."
        asHeading
        className="agent-v2-analytics-visual"
      />

      {wallet && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <DashboardMetric icon={BadgeDollarSign} label="Lifetime Earned" value={fmtUSD(wallet.lifetime_earned_usd)} detail="All commission credits" tone="green" />
          <DashboardMetric icon={Wallet} label="Available Now" value={fmtUSD(wallet.available_usd)} detail="Ready to withdraw" tone="green" />
          <DashboardMetric icon={ShieldCheck} label="Locked" value={fmtUSD(wallet.locked_usd)} detail="Held for pending withdrawals" tone="amber" />
          <DashboardMetric icon={CircleDollarSign} label="Withdrawn" value={fmtUSD(wallet.withdrawn_usd)} detail="Paid out so far" tone="blue" />
        </div>
      )}

      {profitSharing && (
        <Panel className="p-4">
          <ChartHeader icon={CircleDollarSign} title="Profit Sharing" description="After all configured fees. Your commission remains exactly the rate assigned to you; this view does not change your wallet or payout ledger." />
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-[#D4AF37]">You · {Number(profitSharing.agent_share_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</p>
              <strong className="mt-1 block text-xl tabular-nums text-[#D4AF37]">{fmtUSD(profitSharing.agent_share_usd)}</strong>
              <small className="mt-1 block text-gray-400">Your exact commission</small>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Platform Support Team · {Number(profitSharing.platform_support_share_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</p>
              <strong className="mt-1 block text-xl tabular-nums text-white">{fmtUSD(profitSharing.platform_support_share_usd)}</strong>
              <small className="mt-1 block text-gray-500">Tenant support allocation</small>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Company · {Number(profitSharing.company_share_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</p>
              <strong className="mt-1 block text-xl tabular-nums text-white">{fmtUSD(profitSharing.company_share_usd)}</strong>
              <small className="mt-1 block text-gray-500">Automatic remainder</small>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs">
            <span className="text-gray-400">Post-fee profit: <strong className="ml-1 text-white">{fmtUSD(profitSharing.base_profit_usd)}</strong></span>
            <span className="font-bold text-[#D4AF37]">Total {Number(profitSharing.total_share_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}% · {fmtUSD(profitSharing.total_share_usd)}</span>
          </div>
        </Panel>
      )}

      <Panel className="p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <ChartHeader icon={BarChart3} title="Daily Flow" description="Deposits in, cashouts out, and your earnings per day." />
            <div className="flex gap-1 rounded-lg border border-white/10 bg-black/20 p-1">
              {[7, 30, 90].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRange(value)}
                  className={`h-8 rounded-md px-3 text-xs font-bold transition ${range === value ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
                >
                  {value}d
                </button>
              ))}
            </div>
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-white/10 bg-black/20 p-2 text-center"><p className="text-[10px] uppercase tracking-wide text-gray-500">IN</p><strong className="text-sm text-green-400">{fmtUSD(rangeTotals.in_usd)}</strong></div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-2 text-center"><p className="text-[10px] uppercase tracking-wide text-gray-500">OUT</p><strong className="text-sm text-red-400">{fmtUSD(rangeTotals.out_usd)}</strong></div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-2 text-center"><p className="text-[10px] uppercase tracking-wide text-gray-500">Points Used</p><strong className="text-sm text-white">{rangeTotals.points_used.toLocaleString()}</strong></div>
            <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-2 text-center"><p className="text-[10px] uppercase tracking-wide text-[#D4AF37]">You Earned</p><strong className="text-sm text-[#D4AF37]">{fmtUSD(rangeTotals.earned_usd)}</strong></div>
          </div>
          <ChartContainer
            className="agent-profit-chart"
            config={{
              in_usd: { label: 'IN', color: '#16a34a' },
              out_usd: { label: 'OUT', color: '#ef4444' },
              earned_usd: { label: 'Earned', color: '#D4AF37' },
            }}
          >
            <ComposedChart data={chartData} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
              <ChartTooltip content={<ChartTooltipContent formatter={(value) => fmtUSD(value)} />} />
              <Bar dataKey="in_usd" fill="#16a34a" radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Bar dataKey="out_usd" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Line type="monotone" dataKey="earned_usd" stroke="#D4AF37" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ChartContainer>
        </Panel>

      <Panel className="p-4">
        <ChartHeader icon={LineChart} title="Day By Day" description="Only days with movement are listed. Every column is the same math as the formula above." />
        {recentDays.length === 0 ? (
          <EmptyState>No customer movement in this period.</EmptyState>
        ) : (
          <>
          <div className="agent-v2-mobile-records mt-3">
            {recentDays.map((row) => {
              const fees = Number(row.in_fee_usd || 0) + Number(row.out_fee_usd || 0) + Number(row.web_fee_usd || 0) + Number(row.net_house_fee_usd || 0);
              return <article key={`mobile-${row.day}`} className="agent-v2-mobile-record"><div className="agent-v2-mobile-record-head"><div><strong>{formatDayLabel(row.day)}</strong><small>{Number(row.points_used || 0).toLocaleString()} game credits used</small></div><strong>{fmtUSD(row.earned_usd)}</strong></div><dl><div><dt>Money in</dt><dd>{fmtUSD(row.in_usd)}</dd></div><div><dt>Money out</dt><dd>{fmtUSD(row.out_usd)}</dd></div><div><dt>Fees</dt><dd>{fmtUSD(fees)}</dd></div><div><dt>Net profit</dt><dd>{fmtUSD(row.net_profit_usd)}</dd></div></dl></article>;
            })}
          </div>
          <div className="agent-v2-desktop-records mt-3 overflow-x-auto" tabIndex={0} aria-label="Analytics history table">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Day</th>
                  <th className="py-2 pr-3 text-right">IN</th>
                  <th className="py-2 pr-3 text-right">OUT</th>
                  <th className="py-2 pr-3 text-right">Points Used</th>
                  <th className="py-2 pr-3 text-right">Fees</th>
                  <th className="py-2 pr-3 text-right">Net Profit</th>
                  <th className="py-2 text-right">You Earned</th>
                </tr>
              </thead>
              <tbody>
                {recentDays.map((row) => (
                  <tr key={row.day} className="border-b border-white/5">
                    <td className="py-2 pr-3 text-white">{formatDayLabel(row.day)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-green-400">{fmtUSD(row.in_usd)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-red-400">{fmtUSD(row.out_usd)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{Number(row.points_used || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-red-400">
                      {fmtUSD(Number(row.in_fee_usd || 0) + Number(row.out_fee_usd || 0) + Number(row.web_fee_usd || 0) + Number(row.net_house_fee_usd || 0))}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-white">{fmtUSD(row.net_profit_usd)}</td>
                    <td className="py-2 text-right tabular-nums font-bold text-[#D4AF37]">{fmtUSD(row.earned_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Panel>

    </div>
  );
}

function GameRatesSection({ analytics }) {
  const [gameRatePeriod, setGameRatePeriod] = useState('week');
  const [gameView, setGameView] = useState('activity');
  const gameDaily = analytics?.game_daily || [];
  const configuredGameRates = Array.isArray(analytics?.game_rates) ? analytics.game_rates : [];
  const gameRateRows = useMemo(() => aggregateAgentGameRates(gameDaily, gameRatePeriod), [gameDaily, gameRatePeriod]);
  const gameRateTotals = useMemo(() => gameRateRows.reduce((total, game) => ({
    loaded_points: total.loaded_points + Number(game.loaded_points || 0),
    redeemed_points: total.redeemed_points + Number(game.redeemed_points || 0),
    net_points: total.net_points + Number(game.net_points || 0),
    net_house_fee_usd: total.net_house_fee_usd + Number(game.net_house_fee_usd || 0),
  }), { loaded_points: 0, redeemed_points: 0, net_points: 0, net_house_fee_usd: 0 }), [gameRateRows]);

  return (
    <div className="agent-page agent-v2-games-page space-y-5">
      <SectionVisual
        image={gameCreditEngine}
        title="Movement and coin cost stay connected."
        description={gameView === 'activity'
          ? 'Review completed credit activity here, then open Coin Rates whenever you need the live percentage for each game.'
          : 'Coin cost varies by game and is automatically used in your reports and earnings.'}
        action={(
          <div className="agent-game-view-tabs" role="group" aria-label="Game credits views">
            <button type="button" aria-pressed={gameView === 'activity'} className={gameView === 'activity' ? 'is-active' : ''} onClick={() => setGameView('activity')} title="Game credits activity"><LineChart /><span>Activity</span></button>
            <button type="button" aria-pressed={gameView === 'rates'} className={gameView === 'rates' ? 'is-active' : ''} onClick={() => setGameView('rates')} title="Game coin rates"><Gamepad2 /><span>Coin Rates</span></button>
          </div>
        )}
        asHeading
        className="agent-v2-games-visual"
      />
      <Panel className="p-4">
        <div hidden={gameView !== 'rates'}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <ChartHeader icon={CoinStackIcon} title="Coin cost by game" description="These live percentages are automatically used for your reports and earnings." />
          </div>

          {configuredGameRates.length === 0 ? (
            <div className="mt-4"><EmptyState>No working games or per-game rates are configured in Agents Hub yet.</EmptyState></div>
          ) : (
            <>
              <div className="mt-4 space-y-2 md:hidden">
                {configuredGameRates.map((game) => (
                  <article key={game.id || game.code} className="agent-game-rate-card rounded-lg border border-white/10 bg-black/20 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="agent-game-identity min-w-0">
                        <AgentGameIcon game={game} />
                        <div className="min-w-0">
                          <strong className="block truncate text-sm text-white">{game.name || game.code || game.id}</strong>
                          <small className="block text-[11px] text-gray-500">Coin cost rate</small>
                        </div>
                      </div>
                      <strong className="shrink-0 text-base text-[#D4AF37]">{Number(game.rate_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</strong>
                    </div>
                  </article>
                ))}
              </div>
              <div className="mt-4 hidden overflow-x-auto md:block" tabIndex={0} aria-label="Game coin rates table">
                <table className="w-full min-w-[420px] text-sm">
                  <thead><tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-wide text-gray-500"><th className="py-2 pr-3">Working game</th><th className="py-2 text-right">Coin cost rate</th></tr></thead>
                  <tbody>{configuredGameRates.map((game) => (
                    <tr key={game.id || game.code} className="border-b border-white/5">
                      <td className="py-3 pr-3"><div className="agent-game-identity is-compact"><AgentGameIcon game={game} compact /><div><strong className="block text-white">{game.name || game.code || game.id}</strong><small className="text-[10px] text-gray-500">Coin cost rate</small></div></div></td>
                      <td className="py-3 text-right tabular-nums font-bold text-[#D4AF37]">{Number(game.rate_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div hidden={gameView !== 'activity'}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <ChartHeader icon={CoinStackIcon} title="Game Credits Activity" description="Filter a period to review completed credit movement for each game." />
          <div className="flex rounded-lg border border-white/10 bg-black/20 p-1">
            {AGENT_GAME_RATE_PERIODS.map((period) => (
              <button
                key={period.id}
                type="button"
                onClick={() => setGameRatePeriod(period.id)}
                className={`h-8 rounded-md px-2.5 text-[11px] font-bold transition ${gameRatePeriod === period.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {gameRateRows.length === 0 ? (
          <div className="mt-4"><EmptyState>No completed working-game movement in this period.</EmptyState></div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
              <GameRateMetric label="Working games" value={String(gameRateRows.length)} />
              <GameRateMetric label="Points loaded" value={Number(gameRateTotals.loaded_points || 0).toLocaleString()} />
              <GameRateMetric label="Net coins" value={Number(gameRateTotals.net_points || 0).toLocaleString()} />
              <GameRateMetric label="Coin cost" value={fmtUSD(gameRateTotals.net_house_fee_usd)} tone="gold" />
            </div>

            <div className="mt-3 space-y-2 md:hidden">
              {gameRateRows.map((game) => (
                <article key={game.game_id} className="agent-game-activity-card rounded-lg border border-white/10 bg-black/20 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="agent-game-identity min-w-0">
                      <AgentGameIcon game={game} />
                      <div className="min-w-0">
                        <strong className="block truncate text-sm text-white">{game.game_name}</strong>
                        <small className="block text-[11px] text-gray-500">Coin cost rate</small>
                      </div>
                    </div>
                    <strong className="shrink-0 text-sm text-[#D4AF37]">{Number(game.rate_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</strong>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <span className="rounded-md bg-white/5 p-2 text-gray-400">Loaded<strong className="mt-1 block text-sm text-white">{Number(game.loaded_points || 0).toLocaleString()}</strong></span>
                    <span className="rounded-md bg-white/5 p-2 text-gray-400">Redeemed<strong className="mt-1 block text-sm text-white">{Number(game.redeemed_points || 0).toLocaleString()}</strong></span>
                    <span className="rounded-md bg-white/5 p-2 text-gray-400">Net coins<strong className="mt-1 block text-sm text-white">{Number(game.net_points || 0).toLocaleString()}</strong></span>
                    <span className="rounded-md bg-[#D4AF37]/10 p-2 text-[#D4AF37]">Coin cost<strong className="mt-1 block text-sm text-[#D4AF37]">{fmtUSD(game.net_house_fee_usd)}</strong></span>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-3 hidden overflow-x-auto md:block" tabIndex={0} aria-label="Game credits activity table">
              <table className="w-full min-w-[720px] text-sm">
                <thead><tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-wide text-gray-500"><th className="py-2 pr-3">Game</th><th className="py-2 pr-3 text-right">Loaded</th><th className="py-2 pr-3 text-right">Redeemed</th><th className="py-2 pr-3 text-right">Net coins</th><th className="py-2 pr-3 text-right">Coin cost rate</th><th className="py-2 text-right">Coin cost</th></tr></thead>
                <tbody>{gameRateRows.map((game) => (
                  <tr key={game.game_id} className="border-b border-white/5">
                    <td className="py-3 pr-3"><div className="agent-game-identity is-compact"><AgentGameIcon game={game} compact /><div><strong className="block text-white">{game.game_name}</strong><small className="text-[10px] text-gray-500">Coin cost rate</small></div></div></td>
                    <td className="py-3 pr-3 text-right tabular-nums text-gray-300">{Number(game.loaded_points || 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-gray-300">{Number(game.redeemed_points || 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-white">{Number(game.net_points || 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-[#D4AF37]">{Number(game.rate_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</td>
                    <td className="py-3 text-right tabular-nums font-bold text-[#D4AF37]">{fmtUSD(game.net_house_fee_usd)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}
        </div>
      </Panel>
    </div>
  );
}

const utcDayKey = (date) => date.toISOString().slice(0, 10);

const AGENT_GAME_RATE_PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 Days' },
  { id: 'month', label: '30 Days' },
  { id: 'all', label: '90 Days' },
];

const agentGameRateStart = (period) => {
  const now = new Date();
  if (period === 'today') return utcDayKey(now);
  if (period === 'week') return utcDayKey(new Date(now.getTime() - 6 * 86400000));
  if (period === 'month') return utcDayKey(new Date(now.getTime() - 29 * 86400000));
  return '0000-00-00';
};

const aggregateAgentGameRates = (rows = [], period = 'week') => {
  const start = agentGameRateStart(period);
  const grouped = new Map();
  rows.filter((row) => String(row.day || '') >= start).forEach((row) => {
    const gameId = row.game_id || 'unknown';
    const item = grouped.get(gameId) || {
      game_id: gameId,
      game_name: row.game_name || 'Unknown game',
      loaded_points: 0,
      redeemed_points: 0,
      refunded_points: 0,
      net_points: 0,
      net_house_fee_usd: 0,
      rate_percent: Number(row.rate_percent || 0),
      rate_source: row.rate_source || 'default',
    };
    item.loaded_points += Number(row.loaded_points || 0);
    item.redeemed_points += Number(row.redeemed_points || 0);
    item.refunded_points += Number(row.refunded_points || 0);
    item.net_points += Number(row.net_points || 0);
    item.net_house_fee_usd += Number(row.net_house_fee_usd || 0);
    item.rate_percent = Number(row.rate_percent ?? item.rate_percent ?? 0);
    item.rate_source = row.rate_source || item.rate_source;
    grouped.set(gameId, item);
  });
  return Array.from(grouped.values()).sort((a, b) => Math.abs(Number(b.net_house_fee_usd || 0)) - Math.abs(Number(a.net_house_fee_usd || 0)));
};

function GameRateMetric({ label, value, tone = 'plain' }) {
  return <div className={`rounded-lg border p-3 text-center ${tone === 'gold' ? 'border-[#D4AF37]/30 bg-[#D4AF37]/10' : 'border-white/10 bg-black/20'}`}><p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p><strong className={`mt-1 block text-sm tabular-nums ${tone === 'gold' ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</strong></div>;
}

const MONEY_FLOW_RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: '7 Days' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'all', label: 'All 90d' },
];

const moneyFlowWindow = (rangeId) => {
  const now = new Date();
  const today = utcDayKey(now);
  if (rangeId === 'today') return { from: today, to: today };
  if (rangeId === 'yesterday') {
    const day = utcDayKey(new Date(now.getTime() - 86400000));
    return { from: day, to: day };
  }
  if (rangeId === 'week') return { from: utcDayKey(new Date(now.getTime() - 6 * 86400000)), to: today };
  if (rangeId === 'this_month') return { from: `${today.slice(0, 7)}-01`, to: today };
  if (rangeId === 'last_month') {
    const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    return { from: utcDayKey(first), to: utcDayKey(last) };
  }
  return { from: '0000-00-00', to: today };
};

function MoneyFlowSection({ analytics, commissionPercent }) {
  const [rangeId, setRangeId] = useState('this_month');
  const daily = analytics?.daily || [];
  const fees = analytics?.breakdown?.fee_percents || {};
  const pct = analytics?.breakdown?.commission_percent ?? commissionPercent ?? 0;
  const { from, to } = moneyFlowWindow(rangeId);
  // ISO day keys sort lexicographically, so plain string compares filter the window.
  const rows = daily.filter((row) => row.day >= from && row.day <= to);
  const totals = rows.reduce((acc, row) => ({
    in_usd: acc.in_usd + Number(row.in_usd || 0),
    out_usd: acc.out_usd + Number(row.out_usd || 0),
    in_fee_usd: acc.in_fee_usd + Number(row.in_fee_usd || 0),
    out_fee_usd: acc.out_fee_usd + Number(row.out_fee_usd || 0),
    web_fee_usd: acc.web_fee_usd + Number(row.web_fee_usd || 0),
    net_house_fee_usd: acc.net_house_fee_usd + Number(row.net_house_fee_usd || 0),
    points_used: acc.points_used + Number(row.points_used || 0),
    net_profit_usd: acc.net_profit_usd + Number(row.net_profit_usd || 0),
    earned_usd: acc.earned_usd + Number(row.earned_usd || 0),
  }), { in_usd: 0, out_usd: 0, in_fee_usd: 0, out_fee_usd: 0, web_fee_usd: 0, net_house_fee_usd: 0, points_used: 0, net_profit_usd: 0, earned_usd: 0 });
  const gross = totals.in_usd - totals.out_usd;
  const feesTotal = totals.in_fee_usd + totals.out_fee_usd + totals.web_fee_usd + totals.net_house_fee_usd;
  const dayList = [...rows].reverse();
  const activeRange = MONEY_FLOW_RANGES.find((item) => item.id === rangeId) || MONEY_FLOW_RANGES[3];

  if (!analytics) {
    return (
      <div className="agent-page agent-v2-moneyflow-page space-y-5">
        <Panel className="p-6"><EmptyState>No money flow yet.</EmptyState></Panel>
      </div>
    );
  }

  return (
    <div className="agent-page agent-v2-moneyflow-page space-y-5">
      <div className="flex flex-wrap gap-1 rounded-lg border border-white/10 bg-black/20 p-1">
        {MONEY_FLOW_RANGES.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setRangeId(item.id)}
            className={`h-9 rounded-md px-3 text-xs font-bold transition ${rangeId === item.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <DashboardMetric icon={BadgeDollarSign} label="You Earned" value={fmtUSD(totals.earned_usd)} detail={activeRange.label} tone="green" />
        <DashboardMetric icon={Wallet} label="Money IN" value={fmtUSD(totals.in_usd)} detail="Customer deposits" tone="blue" />
        <DashboardMetric icon={CircleDollarSign} label="Money OUT" value={fmtUSD(totals.out_usd)} detail="Customer cashouts" tone="amber" />
        <DashboardMetric icon={ExpensesReceiptIcon} label="Total Fees" value={fmtUSD(feesTotal)} detail="All deductions" tone="amber" />
        <DashboardMetric icon={BarChart3} label="Net Profit" value={fmtUSD(totals.net_profit_usd)} detail={`${Number(totals.points_used || 0).toLocaleString()} points used`} tone="green" />
      </div>

      <Panel className="agent-v2-moneyflow-breakdown space-y-2 p-4">
        <header className="agent-v2-art-card-head">
          <ChartHeader icon={ExpensesReceiptIcon} title={`Breakdown — ${activeRange.label}`} description="The same formula as always, applied to just this period." />
          <img src={moneyFlowPipeline} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" />
        </header>
        <FlowRow label="Customer deposits (IN)" helper="Completed deposits from your customers" amount={totals.in_usd} kind="gain" />
        <FlowRow label="Customer cashouts (OUT)" helper="Completed withdrawals back to customers" amount={totals.out_usd} kind="cut" />
        <FlowRow label="Gross profit" helper="IN minus OUT" amount={gross} kind="total" />
        <FlowRow label={`IN fee (${fees.in ?? 0}%)`} helper="Payment processing on deposits" amount={totals.in_fee_usd} kind="cut" />
        <FlowRow label={`OUT fee (${fees.out ?? 0}%)`} helper="Processing on customer cashouts" amount={totals.out_fee_usd} kind="cut" />
        <FlowRow label={`Web fee (${fees.web ?? 0}%)`} helper="Platform fee on deposits" amount={totals.web_fee_usd} kind="cut" />
        <FlowRow
          label="Game credits fee"
          helper={`${Number(totals.points_used || 0).toLocaleString()} game points used (loaded − redeemed)`}
          amount={totals.net_house_fee_usd}
          kind="cut"
        />
        <FlowRow label="Net profit" helper="What remains after every fee" amount={totals.net_profit_usd} kind="total" />
        <FlowRow label={`You earn ${pct}%`} helper="Your commission on the net profit" amount={totals.earned_usd} kind="final" />
      </Panel>

      <Panel className="p-4">
        <ChartHeader icon={LineChart} title={`Every Day — ${activeRange.label}`} description="The full period, day by day. Quiet days stay visible so nothing is hidden." />
        <div className="agent-v2-mobile-records mt-3">
          {dayList.map((row) => {
            const rowFees = Number(row.in_fee_usd || 0) + Number(row.out_fee_usd || 0) + Number(row.web_fee_usd || 0) + Number(row.net_house_fee_usd || 0);
            return <article key={`mobile-${row.day}`} className="agent-v2-mobile-record"><div className="agent-v2-mobile-record-head"><div><strong>{formatDayLabel(row.day)}</strong><small>{Number(row.points_used || 0).toLocaleString()} game credits used</small></div><strong>{fmtUSD(row.earned_usd)}</strong></div><dl><div><dt>Money in</dt><dd>{fmtUSD(row.in_usd)}</dd></div><div><dt>Money out</dt><dd>{fmtUSD(row.out_usd)}</dd></div><div><dt>Fees</dt><dd>{fmtUSD(rowFees)}</dd></div><div><dt>Net profit</dt><dd>{fmtUSD(row.net_profit_usd)}</dd></div></dl></article>;
          })}
        </div>
        <div className="agent-v2-desktop-records mt-3 overflow-x-auto" tabIndex={0} aria-label="Expense cutoff history table">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3">Day</th>
                <th className="py-2 pr-3 text-right">IN</th>
                <th className="py-2 pr-3 text-right">OUT</th>
                <th className="py-2 pr-3 text-right">Points Used</th>
                <th className="py-2 pr-3 text-right">Fees</th>
                <th className="py-2 pr-3 text-right">Net Profit</th>
                <th className="py-2 text-right">You Earned</th>
              </tr>
            </thead>
            <tbody>
              {dayList.map((row) => {
                const quiet = !row.in_usd && !row.out_usd && !row.points_used && !row.earned_usd;
                const rowFees = Number(row.in_fee_usd || 0) + Number(row.out_fee_usd || 0) + Number(row.web_fee_usd || 0) + Number(row.net_house_fee_usd || 0);
                return (
                  <tr key={row.day} className={`border-b border-white/5 ${quiet ? 'opacity-40' : ''}`}>
                    <td className="py-2 pr-3 text-white">{formatDayLabel(row.day)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-green-400">{fmtUSD(row.in_usd)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-red-400">{fmtUSD(row.out_usd)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{Number(row.points_used || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-red-400">{fmtUSD(rowFees)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-white">{fmtUSD(row.net_profit_usd)}</td>
                    <td className="py-2 text-right tabular-nums font-bold text-[#D4AF37]">{fmtUSD(row.earned_usd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function FeeCard({ name, rate, rateLabel, appliesTo, explanation, tone = 'red' }) {
  const rateTone = tone === 'gold' ? 'text-[#D4AF37]' : 'text-red-400';
  return (
    <Panel className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-white">{name}</h3>
          <p className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500">{appliesTo}</p>
        </div>
        <strong className={`shrink-0 text-2xl font-black tabular-nums ${rateTone}`}>{rateLabel || `${Number(rate || 0)}%`}</strong>
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-400">{explanation}</p>
    </Panel>
  );
}

function FeesSection({ analytics }) {
  const rules = analytics?.fee_rules || null;

  if (!rules) {
    return (
      <div className="agent-page agent-v2-fees-page space-y-5">
        <Panel className="p-6"><EmptyState>No fee rules configured yet.</EmptyState></Panel>
      </div>
    );
  }

  return (
    <div className="agent-page agent-v2-fees-page space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <FeeCard
          name="IN Fee"
          rate={rules.in_fee_percent}
          appliesTo="On every customer deposit"
          explanation="Covers payment processing when your customers deposit. Taken from the deposit amount before profit is counted."
        />
        <FeeCard
          name="OUT Fee"
          rate={rules.out_fee_percent}
          appliesTo="On every customer cashout"
          explanation="Covers processing when customers withdraw their winnings. Taken on the cashout amount."
        />
        <FeeCard
          name="Web Fee"
          rate={rules.web_fee_percent}
          appliesTo="On every customer deposit"
          explanation="The platform's own fee for running the site, calculated on deposits like the IN fee."
        />
        <FeeCard
          name="Game Credits Fee"
          rateLabel="By game"
          appliesTo="On game credits used (loaded − redeemed)"
          explanation="Coin cost varies by game. The exact live percentages appear under Game Coin Rates."
        />
      </div>

      <Panel className="agent-v2-expenses-summary border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
        <div className="agent-v2-expenses-summary-copy">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-[#D4AF37]">Your Commission</h3>
              <p className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500">On the net profit after every fee above</p>
            </div>
            <strong className="shrink-0 text-3xl font-black tabular-nums text-[#D4AF37]">{Number(rules.commission_percent || 0)}%</strong>
          </div>
          <p className="mt-2 text-xs leading-5 text-gray-400">
            Everything left after the four fees is the net profit. You keep {Number(rules.commission_percent || 0)}% of it — when it is negative, the negative carries into your balance too, so the number is always honest.
          </p>
        </div>
        <img src={expensesCutoffLayers} width="768" height="768" loading="lazy" decoding="async" alt="" aria-hidden="true" draggable="false" />
      </Panel>
    </div>
  );
}

function HowItWorksSection({ items }) {
  const fallback = [
    { title: 'How you earn', body: 'Share your referral link. Customers who join from it are tracked under your agent account.' },
    { title: 'Customer referrals', body: 'When your customer invites another customer, the referral tree stays visible in your portal.' },
    { title: 'Team structure', body: 'Customer chat is separate from internal team requests.' },
    { title: '24/7 support', body: 'Reach support, confirmations, managers, communication, or development from Team Chat.' },
  ];
  const rows = items.length ? items : fallback;
  return (
    <div className="agent-page agent-v2-how-page space-y-5">
      <Panel className="agent-v2-how-hero">
        <div>
          <span className="agent-v2-kicker"><HowItWorksIcon /> One connected workflow</span>
          <h3>From your customer link to a verified payout.</h3>
          <p>Each stage is recorded in the portal, so you can support customers and understand exactly how your earnings were calculated.</p>
        </div>
        <img src={howItWorksFlow} width="1774" height="887" loading="lazy" decoding="async" alt="" aria-hidden="true" />
      </Panel>
      <div className="agent-v2-how-timeline">
        {rows.map((item, index) => (
          <Panel key={item.title} className="agent-v2-how-step">
            <span>{String(index + 1).padStart(2, '0')}</span>
            <div><h3>{item.title}</h3><p>{item.body}</p></div>
          </Panel>
        ))}
      </div>
      <Panel className="agent-v2-privacy-note">
        <ShieldCheck />
        <div><strong>Your public profile, your private account</strong><p>Attributed customers may see the name, photo, title, and bio you choose. Your private contact details stay hidden, while authorized master auditing remains available for safety.</p></div>
      </Panel>
    </div>
  );
}

function LedgerSection({ ledger, customerActivity, pointsMovements }) {
  return (
    <div className="agent-page agent-v2-ledger-page space-y-5">
      <SectionVisual
        image={ledgerProofTrail}
        title="Every movement leaves a clear trail."
        description="Earnings, customer activity, and game-credit entries remain separated and easy to verify."
        asHeading
        className="agent-v2-ledger-visual"
      />
      <Panel className="agent-ledger-panel">
        <Tabs defaultValue="earnings" className="agent-ledger-tabs">
          <TabsList className="agent-ledger-tab-list">
            <TabsTrigger value="earnings">Agent Earnings <span>{ledger.length}</span></TabsTrigger>
            <TabsTrigger value="activity">Customer Activity <span>{customerActivity.length}</span></TabsTrigger>
            <TabsTrigger value="points">Points Movements <span>{pointsMovements.length}</span></TabsTrigger>
          </TabsList>
          <TabsContent value="earnings" className="agent-ledger-content">
            {ledger.length === 0 ? (
              <EmptyState>No earning events yet.</EmptyState>
            ) : (
              <div className="space-y-2">
                {ledger.map((row) => <LedgerRow key={row.id} row={row} />)}
              </div>
            )}
          </TabsContent>
          <TabsContent value="activity" className="agent-ledger-content">
            <CustomerActivityList rows={customerActivity} />
          </TabsContent>
          <TabsContent value="points" className="agent-ledger-content">
            <PointsMovementTable rows={pointsMovements} />
          </TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || 'pending').toLowerCase();
  const complete = ['approved', 'completed', 'paid', 'success', 'successful', 'active'].includes(normalized);
  const failed = ['failed', 'rejected', 'cancelled', 'canceled', 'disabled'].includes(normalized);
  return <span className={`agent-status-badge ${complete ? 'is-complete' : failed ? 'is-failed' : 'is-pending'}`}>{normalized.replaceAll('_', ' ')}</span>;
}

function CustomerActivityList({ rows }) {
  if (!rows?.length) return <EmptyState>No customer activity yet.</EmptyState>;
  return (
    <div className="agent-activity-list">
      {rows.map((row) => {
        const isPayment = row.kind === 'deposit' || row.kind === 'withdrawal';
        const paymentMethodVisual = row.kind === 'deposit' ? paymentMethodVisualFor(row.method) : null;
        const Icon = isPayment ? CircleDollarSign : Gamepad2;
        return (
          <article key={row.id} className="agent-activity-row">
            <div className={`agent-activity-icon kind-${row.kind} ${paymentMethodVisual ? 'has-payment-method' : ''}`}>
              {paymentMethodVisual
                ? <img src={paymentMethodVisual.icon} alt={`${paymentMethodVisual.label} payment`} />
                : <Icon />}
            </div>
            <div className="agent-activity-copy">
              <div><strong>{row.label || String(row.kind || 'Activity').replaceAll('_', ' ')}</strong><StatusBadge status={row.status} /></div>
              <p>
                {row.player_name || row.player_id || 'Customer'}
                {row.game_name ? ` · ${row.game_name}` : ''}
                {paymentMethodVisual ? ` · ${paymentMethodVisual.label}` : ''}
              </p>
              <span>{fmtDate(row.created_at)} · Ref {row.reference || '-'}</span>
            </div>
            <strong className={`agent-activity-amount ${row.kind === 'withdrawal' || row.kind === 'game_redeem' ? 'is-negative' : 'is-positive'}`}>
              {isPayment ? fmtUSD(row.amount_usd) : `${Number(row.points || 0).toLocaleString()} pts`}
            </strong>
          </article>
        );
      })}
    </div>
  );
}

function PointsMovementTable({ rows }) {
  if (!rows?.length) return <EmptyState>No game point movements yet.</EmptyState>;
  return (
    <>
      <div className="agent-v2-mobile-records">
        {rows.map((row) => (
          <article key={`mobile-${row.id}`} className="agent-v2-mobile-record">
            <div className="agent-v2-mobile-record-head"><div><strong>{row.player_name || 'Customer'}</strong><small>{row.game_name || 'Game'}</small></div><StatusBadge status={row.status} /></div>
            <dl><div><dt>Movement</dt><dd>{String(row.kind || 'movement').replaceAll('_', ' ')}</dd></div><div><dt>Points</dt><dd className={row.kind === 'redeem' ? 'is-negative' : 'is-positive'}>{Number(row.points || 0).toLocaleString()}</dd></div></dl>
            <p>{fmtDate(row.created_at)} · Ref {row.reference || '-'}</p>
          </article>
        ))}
      </div>
      <div className="agent-table-scroll agent-v2-desktop-records" tabIndex={0} aria-label="Game points ledger table">
        <table className="agent-data-table agent-points-table">
          <thead><tr><th>Customer</th><th>Game</th><th>Movement</th><th>Points</th><th>Status</th><th>Time / Reference</th></tr></thead>
          <tbody>
            {rows.map((row) => (
            <tr key={row.id}>
              <td><strong>{row.player_name || 'Customer'}</strong><span>{row.player_id}</span></td>
              <td><strong>{row.game_name || 'Game'}</strong><span>{row.game_id}</span></td>
              <td className="capitalize">{String(row.kind || 'movement').replaceAll('_', ' ')}</td>
              <td className={row.kind === 'redeem' ? 'is-negative' : 'is-positive'}>{Number(row.points || 0).toLocaleString()}</td>
              <td><StatusBadge status={row.status} /></td>
              <td><strong>{fmtDate(row.created_at)}</strong><span>{row.reference || '-'}</span></td>
            </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function WithdrawalsSection({ withdrawals, balance, wallet, onWithdraw }) {
  const available = wallet?.available_usd ?? balance ?? 0;
  const pendingCount = withdrawals.filter((row) => !['approved', 'completed', 'paid', 'rejected', 'failed', 'cancelled', 'canceled'].includes(String(row.status || '').toLowerCase())).length;
  return (
    <div className="agent-page agent-v2-withdrawals-page space-y-5">
      <Panel className="agent-v2-payout-hero">
        <div className="agent-v2-payout-balance">
          <span className="agent-v2-kicker agent-v2-payout-kicker"><WithdrawActionIcon /> Available to withdraw</span>
          <strong>{fmtUSD(available)}</strong>
          <p>{pendingCount ? `${pendingCount} payout request${pendingCount === 1 ? '' : 's'} currently under review.` : 'No payout request is currently pending.'}</p>
          <button type="button" onClick={onWithdraw}><WithdrawActionIcon /> Request a payout</button>
        </div>
        <div className="agent-v2-payout-art"><img src={withdrawalReassurance} width="1774" height="887" loading="lazy" decoding="async" alt="" aria-hidden="true" /></div>
      </Panel>
      <div className="agent-v2-wallet-strip">
        <DashboardMetric icon={ShieldCheck} label="Reserved" value={fmtUSD(wallet?.locked_usd)} detail="Pending payout requests" tone="amber" />
        <DashboardMetric icon={CircleDollarSign} label="Withdrawn" value={fmtUSD(wallet?.withdrawn_usd)} detail="Approved payouts" tone="blue" />
        <DashboardMetric icon={BadgeDollarSign} label="Lifetime earned" value={fmtUSD(wallet?.lifetime_earned_usd)} detail="Credited commissions" tone="green" />
      </div>
      <Panel className="agent-v2-withdraw-history">
        <div className="agent-v2-section-bar"><ChartHeader icon={WithdrawalHistoryIcon} title="Payout history" description="Your latest requests and their current status." /><span>{withdrawals.length} recent</span></div>
        {withdrawals.length === 0 ? (
          <div className="agent-v2-payout-empty">
            <img src={payoutHistoryEmptySpot} width="960" height="640" loading="lazy" decoding="async" alt="" aria-hidden="true" />
            <EmptyState icon={WithdrawalHistoryIcon} title="No payout requests yet">When you request your first withdrawal, its full status history will appear here.</EmptyState>
          </div>
        ) : (
          <div className="agent-v2-record-list">
            {withdrawals.map((row) => <WithdrawalRow key={row.id} row={row} />)}
          </div>
        )}
      </Panel>
    </div>
  );
}

function AssignmentMini({ assignment }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate font-bold">{assignment.tenant?.name || assignment.tenant_id}</p>
        <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full ${assignment.active === false ? 'bg-red-500/15 text-red-300' : 'bg-green-500/15 text-green-300'}`}>{assignment.active === false ? 'Paused' : 'Active'}</span>
      </div>
      <p className="mt-1 truncate text-[11px] text-gray-500">{assignment.commission_basis} · {assignment.commission_percent}%</p>
    </div>
  );
}

function LedgerRow({ row }) {
  const title = formatLedgerEventLabel(row.reason || row.event_type);
  const subtitle = row.reason && row.event_type ? row.event_type : row.description;
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate font-semibold capitalize">{title}</p>
        <p className="text-xs text-gray-500">{subtitle ? `${subtitle} · ` : ''}{fmtDate(row.created_at)}</p>
      </div>
      <p className={`shrink-0 font-black tabular-nums ${row.direction === 'DEBIT' ? 'text-red-300' : 'text-green-300'}`}>
        {row.direction === 'DEBIT' ? '-' : '+'}{fmtUSD(row.amount_usd)}
      </p>
    </div>
  );
}

function WithdrawalRow({ row }) {
  const status = String(row.status || 'pending').toLowerCase();
  return (
    <article className="agent-v2-withdraw-row">
      <span className="agent-v2-withdraw-row-icon"><WithdrawalHistoryIcon /></span>
      <div className="agent-v2-withdraw-row-copy">
        <div><strong>{fmtUSD(row.amount_usd)}</strong><StatusBadge status={status} /></div>
        <p>{String(row.method || 'Payout').replaceAll('_', ' ')}</p>
        <small>{fmtDate(row.created_at)}{row.id ? ` · Ref ${String(row.id).slice(0, 8)}` : ''}</small>
      </div>
      <ChevronRight />
    </article>
  );
}
